import { useState, useEffect, useRef, useCallback } from "react";
import { tokens } from "../theme";
import {
  getAdminPortalSettings, getClientBranding, updateClientBranding,
  uploadClientLogo, deleteClientLogo, deleteClient,
  getAgents, createAgent, updateAgent, deactivateAgent,
  getSLAPolicies, createSLAPolicy, updateSLAPolicy, deleteSLAPolicy,
  getGeneralSettings, updateGeneralSettings,
  getProvisionTokens, createProvisionToken, revokeProvisionToken,
  getMeshcentralNodes, syncMeshcentral,
  updateMyProfile, uploadSignatureImage,
  getRmmStatus,
  createBootstrapToken, getActiveBootstrapToken,
} from "../api";
import { useAuth } from "../context/AuthContext";
import {
  Settings, Building2, Palette, Upload, Trash2,
  ExternalLink, Check, AlertCircle, ChevronRight, X, Eye, Monitor, User, Lock,
} from "lucide-react";
import SecureLinkModal from "../components/SecureLinkModal";
import { getSecureLinks, revokeSecureLink } from "../api";
import useIsMobile from "../hooks/useIsMobile";
import ThemePicker from "../components/ThemePicker";

// =============================================================
//  SHARED STYLE TOKENS
// =============================================================
const T = {
  input:  { background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius:8, padding:"9px 12px", color:tokens.text.primary, fontSize:13, width:"100%", outline:"none", boxSizing:"border-box" },
  label:  { display:"block", fontSize:12, fontWeight:600, color:tokens.text.muted, marginBottom:6 },
  card:   { background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius:10, padding:20, marginBottom:16 },
  cardH:  { fontSize:15, fontWeight:600, color:tokens.text.primary, marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" },
  btn:    (c="#4f6fff",t="#fff") => ({ background:c, color:t, border:"none", borderRadius:7, padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer" }),
  btnSm:  (c,t) => ({ background: c || tokens.surface.inputHi, color: t || tokens.text.muted, border:"none", borderRadius:6, padding:"5px 12px", fontSize:11, fontWeight:500, cursor:"pointer" }),
  table:  { width:"100%", borderCollapse:"collapse" },
  th:     { textAlign:"left", padding:"8px 12px", fontSize:11, color:tokens.text.label, borderBottom:`1px solid ${tokens.border.soft}`, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px" },
  td:     { padding:"10px 12px", fontSize:13, color:tokens.text.body, borderBottom:`1px solid ${tokens.border.softer}` },
  badge:  (c) => ({ display:"inline-flex", alignItems:"center", padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:600, background:c+"22", color:c }),
  grid2:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  modal:  { position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50, padding:20 },
  mbox:   { background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius:14, padding:24, width:"100%", maxWidth:460 },
  err:    { background:"#450a0a", border:"1px solid #7f1d1d", borderRadius:6, padding:"8px 12px", fontSize:12, color:"#fca5a5", marginBottom:12 },
  ok:     { background:"#052e16", border:"1px solid #166534", borderRadius:6, padding:"8px 12px", fontSize:12, color:"var(--badge-green)", marginBottom:12 },
  spin:   { width:16, height:16, border:"2px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block" },
};

const ROLES      = ["admin","manager","technician","dev"];
const PRIORITIES = ["Critical","High","Medium","Low"];
const TIMEZONES  = ["America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","UTC","Europe/London","Europe/Paris","Australia/Sydney"];
const roleColor  = (r) => ({ admin:"#8b5cf6", agent:"#3b82f6", client:"#10b981" }[r] || "#64748b");
const fmtDate    = (d) => d ? new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";
const fmtTime    = (d) => d ? new Date(d).toLocaleString() : "—";
const API_URL    = process.env.REACT_APP_API_URL || "";
const slugify    = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

// =============================================================
//  SHARED COMPONENTS
// =============================================================
function Spinner() { return <span style={T.spin} />; }

function Confirm({ message, onConfirm, onCancel }) {
  return (
    <div style={T.modal}>
      <div style={T.mbox}>
        <p style={{ fontSize:14, color:"var(--t-text-primary)", lineHeight:1.6, marginBottom:20 }}>{message}</p>
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button style={T.btnSm()} onClick={onCancel}>Cancel</button>
          <button style={T.btn("#dc2626")} onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  TAB 1 — USER MANAGEMENT
// =============================================================
function UserManagement() {
  const isMobile = useIsMobile();
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modal,        setModal]        = useState(null);
  const [editing,      setEditing]      = useState(null);
  const [confirm,      setConfirm]      = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState({ first_name:"", last_name:"", email:"", roles:["technician"], password:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getAgents(); setUsers(r.data); } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const toggleRole = (r) => setForm(f => {
    const has = f.roles.includes(r);
    const next = has ? f.roles.filter(x => x !== r) : [...f.roles, r];
    return { ...f, roles: next.length ? next : [r] };
  });

  const openCreate = () => {
    setForm({ first_name:"", last_name:"", email:"", roles:["technician"], password:"" });
    setEditing(null); setModal("open"); setMsg(null);
  };

  const openEdit = (u) => {
    const roles = u.roles?.length ? u.roles : [u.role];
    setForm({ first_name:u.first_name, last_name:u.last_name, email:u.email, roles, password:"" });
    setEditing(u); setModal("open"); setMsg(null);
  };

  const handleSave = async () => {
    if (!form.first_name || !form.email)
      return setMsg({ type:"err", text:"First name and email are required." });
    if (!editing && !form.password)
      return setMsg({ type:"err", text:"Password is required for new users." });
    setSaving(true);
    try {
      const roles = form.roles.length ? form.roles : ["technician"];
      if (editing) {
        const payload = { first_name:form.first_name, last_name:form.last_name, email:form.email, roles, role:roles[0] };
        if (form.password && form.password.trim() !== "") payload.password = form.password.trim();
        await updateAgent(editing.id, payload);
      } else {
        await createAgent({ first_name:form.first_name, last_name:form.last_name, email:form.email, roles, role:roles[0], password:form.password });
      }
      setMsg({ type:"ok", text: editing ? "User updated." : "User created." });
      await load();
      setTimeout(() => { setModal(null); setMsg(null); }, 900);
    } catch(e) {
      setMsg({ type:"err", text: e?.response?.data?.error || "Failed to save." });
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (u) => {
    try { await deactivateAgent(u.id); setConfirm(null); await load(); } catch(e) { console.error(e); }
  };

  const visible = users.filter(u => showInactive ? true : u.active !== false);

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1100 }}>
      <div style={{ ...T.card, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={T.cardH}>
          <span>Users ({visible.length})</span>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <label style={{ fontSize:12, color:"var(--t-text-label)", display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
            <button style={T.btn()} onClick={openCreate}>+ New User</button>
          </div>
        </div>
        {loading ? <div style={{ padding:40, textAlign:"center" }}><Spinner /></div> : (
          <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
            <thead>
              <tr>{["Name","Email","Role","Status","Created","Actions"].map(h => <th key={h} style={T.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map(u => (
                <tr key={u.id} style={{ opacity: u.active===false ? 0.5 : 1 }}>
                  <td style={{ ...T.td, fontWeight:500 }}>{u.first_name} {u.last_name}</td>
                  <td style={T.td}>{u.email}</td>
                  <td style={T.td}>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {(u.roles?.length ? u.roles : [u.role]).map(r => (
                        <span key={r} style={T.badge(roleColor(r))}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td style={T.td}><span style={T.badge(u.active===false ? "#64748b" : "#22c55e")}>{u.active===false ? "Inactive" : "Active"}</span></td>
                  <td style={T.td}>{fmtDate(u.created_at)}</td>
                  <td style={T.td}>
                    <div style={{ display:"flex", gap:6 }}>
                      <button style={T.btnSm("#1e3a5f","#93c5fd")} onClick={() => openEdit(u)}>Edit</button>
                      {u.active !== false && (
                        <button style={T.btnSm("#450a0a","#fca5a5")} onClick={() => setConfirm(u)}>Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div style={T.modal}>
          <div style={T.mbox}>
            <div style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", marginBottom:16 }}>
              {editing ? `Edit — ${editing.first_name} ${editing.last_name}` : "Create New User"}
            </div>
            {msg && <div style={msg.type==="err" ? T.err : T.ok}>{msg.text}</div>}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={T.grid2}>
                <div><label style={T.label}>First Name *</label><input style={T.input} value={form.first_name} onChange={e => set("first_name",e.target.value)} /></div>
                <div><label style={T.label}>Last Name</label><input style={T.input} value={form.last_name} onChange={e => set("last_name",e.target.value)} /></div>
              </div>
              <div><label style={T.label}>Email *</label><input style={T.input} type="email" value={form.email} onChange={e => set("email",e.target.value)} /></div>
              <div>
                <label style={T.label}>Roles (select one or more)</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:4 }}>
                  {ROLES.map(r => {
                    const active = form.roles.includes(r);
                    return (
                      <label key={r} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer",
                        background: active ? roleColor(r)+"22" : "var(--t-surface-mid)",
                        border: `1px solid ${active ? roleColor(r) : "var(--t-border-strong)"}`,
                        borderRadius:6, padding:"5px 12px", fontSize:12, fontWeight:600,
                        color: active ? roleColor(r) : "#64748b", userSelect:"none" }}>
                        <input type="checkbox" checked={active} onChange={() => toggleRole(r)} style={{ display:"none" }} />
                        {r}
                      </label>
                    );
                  })}
                </div>
                {form.roles.length === 0 && <div style={{ fontSize:11, color:"#f87171", marginTop:4 }}>At least one role required.</div>}
              </div>
              <div>
                <label style={T.label}>{editing ? "New Password (leave blank to keep current)" : "Password *"}</label>
                <input style={T.input} type="password" value={form.password} onChange={e => set("password",e.target.value)}
                  placeholder={editing ? "Enter new password to change it" : ""} autoComplete="new-password" />
                {editing && <div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:4 }}>Leave blank to keep the current password unchanged.</div>}
              </div>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:20 }}>
              <button style={T.btnSm()} onClick={() => { setModal(null); setMsg(null); }}>Cancel</button>
              <button style={T.btn()} onClick={handleSave} disabled={saving}>
                {saving ? <Spinner /> : editing ? "Save Changes" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <Confirm
          message={`Deactivate ${confirm.first_name} ${confirm.last_name}? Their account will be disabled but all ticket history is preserved.`}
          onConfirm={() => handleDeactivate(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// =============================================================
//  TAB 2 — SLA POLICIES
// =============================================================
function SLAPolicies() {
  const isMobile = useIsMobile();
  const [policies, setPolicies] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const [form,     setForm]     = useState({ name:"", description:"", response_hours:{ Critical:1, High:4, Medium:8, Low:24 }, resolve_hours:{ Critical:4, High:8, Medium:24, Low:48 } });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getSLAPolicies(); setPolicies(r.data); } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name:"", description:"", response_hours:{Critical:1,High:4,Medium:8,Low:24}, resolve_hours:{Critical:4,High:8,Medium:24,Low:48} });
    setEditing(null); setModal(true); setMsg(null);
  };

  const openEdit = (p) => {
    setForm({ name:p.name, description:p.description||"", response_hours:{...p.response_hours}, resolve_hours:{...p.resolve_hours} });
    setEditing(p); setModal(true); setMsg(null);
  };

  const handleSave = async () => {
    if (!form.name) return setMsg({ type:"err", text:"Policy name is required." });
    setSaving(true);
    try {
      editing ? await updateSLAPolicy(editing.id, form) : await createSLAPolicy(form);
      setMsg({ type:"ok", text: editing ? "Policy updated." : "Policy created." });
      await load();
      setTimeout(() => { setModal(false); setMsg(null); }, 800);
    } catch(e) { setMsg({ type:"err", text:"Failed to save policy." }); }
    finally { setSaving(false); }
  };

  const setHours = (type, priority, val) =>
    setForm(f => ({...f, [type]: {...f[type], [priority]: parseInt(val)||0}}));

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1100 }}>
      <div style={{ ...T.card, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={T.cardH}>
          <span>SLA Policies ({policies.length})</span>
          <button style={T.btn()} onClick={openCreate}>+ New Policy</button>
        </div>
        {loading ? <div style={{ padding:40, textAlign:"center" }}><Spinner /></div> :
         policies.length === 0 ? <p style={{ color:"var(--t-text-label)", fontSize:13 }}>No SLA policies yet.</p> : (
          <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
            <thead><tr>{["Policy Name","Description","Response (hrs)","Resolve (hrs)","Actions"].map(h => <th key={h} style={T.th}>{h}</th>)}</tr></thead>
            <tbody>
              {policies.map(p => (
                <tr key={p.id}>
                  <td style={{ ...T.td, fontWeight:600 }}>{p.name}</td>
                  <td style={{ ...T.td, color:"var(--t-text-label)" }}>{p.description||"—"}</td>
                  <td style={T.td}>
                    <div style={{ fontSize:11, lineHeight:1.9 }}>
                      {PRIORITIES.map(pr => <div key={pr}><span style={{ color:"var(--t-text-label)" }}>{pr}: </span><span style={{ color:"#67e8f9" }}>{p.response_hours?.[pr]||"—"}h</span></div>)}
                    </div>
                  </td>
                  <td style={T.td}>
                    <div style={{ fontSize:11, lineHeight:1.9 }}>
                      {PRIORITIES.map(pr => <div key={pr}><span style={{ color:"var(--t-text-label)" }}>{pr}: </span><span style={{ color:"var(--badge-green)" }}>{p.resolve_hours?.[pr]||"—"}h</span></div>)}
                    </div>
                  </td>
                  <td style={T.td}>
                    <div style={{ display:"flex", gap:6 }}>
                      <button style={T.btnSm("#1e3a5f","#93c5fd")} onClick={() => openEdit(p)}>Edit</button>
                      <button style={T.btnSm("#450a0a","#fca5a5")} onClick={() => setConfirm(p)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div style={T.modal}>
          <div style={{ ...T.mbox, maxWidth:520 }}>
            <div style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", marginBottom:16 }}>{editing ? "Edit SLA Policy" : "New SLA Policy"}</div>
            {msg && <div style={msg.type==="err" ? T.err : T.ok}>{msg.text}</div>}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div><label style={T.label}>Policy Name *</label><input style={T.input} value={form.name} onChange={e => setForm(f => ({...f,name:e.target.value}))} /></div>
              <div><label style={T.label}>Description</label><input style={T.input} value={form.description} onChange={e => setForm(f => ({...f,description:e.target.value}))} /></div>
              <div style={T.grid2}>
                <div>
                  <label style={{ ...T.label, color:"#67e8f9", marginBottom:10 }}>Response Time (hours)</label>
                  {PRIORITIES.map(pr => (
                    <div key={pr} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                      <span style={{ fontSize:12, color:"var(--t-text-label)", width:60 }}>{pr}</span>
                      <input type="number" min="0" style={{ ...T.input, width:72 }} value={form.response_hours[pr]} onChange={e => setHours("response_hours",pr,e.target.value)} />
                      <span style={{ fontSize:11, color:"var(--t-text-deep)" }}>hrs</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label style={{ ...T.label, color:"var(--badge-green)", marginBottom:10 }}>Resolve Time (hours)</label>
                  {PRIORITIES.map(pr => (
                    <div key={pr} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                      <span style={{ fontSize:12, color:"var(--t-text-label)", width:60 }}>{pr}</span>
                      <input type="number" min="0" style={{ ...T.input, width:72 }} value={form.resolve_hours[pr]} onChange={e => setHours("resolve_hours",pr,e.target.value)} />
                      <span style={{ fontSize:11, color:"var(--t-text-deep)" }}>hrs</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:20 }}>
              <button style={T.btnSm()} onClick={() => setModal(false)}>Cancel</button>
              <button style={T.btn()} onClick={handleSave} disabled={saving}>{saving ? <Spinner /> : "Save Policy"}</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <Confirm
          message={`Delete "${confirm.name}"? Clients assigned to this policy will have no SLA. This cannot be undone.`}
          onConfirm={async () => { try { await deleteSLAPolicy(confirm.id); setConfirm(null); await load(); } catch(e){} }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// =============================================================
//  TAB 3 — GENERAL SETTINGS
// =============================================================
function GeneralSettings() {
  const isMobile = useIsMobile();
  const [form,    setForm]    = useState({ company_name:"MIDTC", support_email:"", support_phone:"", timezone:"America/New_York", logo_url:"", primary_color:"#4f6fff" });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    getGeneralSettings()
      .then(r => setForm(f => ({...f,...r.data})))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try { await updateGeneralSettings(form); setMsg({ type:"ok", text:"Settings saved." }); }
    catch(e) { setMsg({ type:"err", text:"Failed to save settings." }); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding:60, textAlign:"center" }}><Spinner /></div>;

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:720 }}>
      {msg && <div style={msg.type==="err" ? T.err : T.ok}>{msg.text}</div>}
      <div style={T.card}>
        <div style={T.cardH}>Company Information</div>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={T.grid2}>
            <div><label style={T.label}>Company Name</label><input style={T.input} value={form.company_name} onChange={e => set("company_name",e.target.value)} /></div>
            <div><label style={T.label}>Support Email</label><input style={T.input} type="email" value={form.support_email} onChange={e => set("support_email",e.target.value)} /></div>
          </div>
          <div style={T.grid2}>
            <div><label style={T.label}>Support Phone</label><input style={T.input} value={form.support_phone} onChange={e => set("support_phone",e.target.value)} /></div>
            <div>
              <label style={T.label}>Timezone</label>
              <select style={T.input} value={form.timezone} onChange={e => set("timezone",e.target.value)}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div style={T.card}>
        <div style={T.cardH}>Branding</div>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div><label style={T.label}>Logo URL</label><input style={T.input} value={form.logo_url} placeholder="https://..." onChange={e => set("logo_url",e.target.value)} /></div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1 }}>
              <label style={T.label}>Primary Color</label>
              <input style={T.input} value={form.primary_color} onChange={e => set("primary_color",e.target.value)} placeholder="#4f6fff" />
            </div>
            <div style={{ width:40, height:40, borderRadius:8, background:form.primary_color, border:"1px solid var(--t-border-strong)", marginTop:16, flexShrink:0 }} />
          </div>
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end" }}>
        <button style={T.btn()} onClick={handleSave} disabled={saving}>{saving ? <Spinner /> : "Save Settings"}</button>
      </div>
    </div>
  );
}

// =============================================================
//  TAB 4 — PROVISIONING TOKENS
// =============================================================
function ProvisioningTokens() {
  const isMobile = useIsMobile();
  const [tokens,   setTokens]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirm,  setConfirm]  = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [newBootstrap, setNewBootstrap] = useState(null);
  const [label,    setLabel]    = useState("");
  const [creatingBoot, setCreatingBoot] = useState(false);
  const [activeBoot, setActiveBoot] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getProvisionTokens();
      setTokens(r.data);
      // Also fetch current active bootstrap (404 if none exists yet)
      try { const b = await getActiveBootstrapToken(); setActiveBoot(b.data); }
      catch { setActiveBoot(null); }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await createProvisionToken({ label: label || "Token " + new Date().toLocaleDateString() });
      setNewToken(r.data.token); setLabel(""); await load();
    } catch(e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleCreateBootstrap = async () => {
    if (!window.confirm(
      "Generate a new RMM bootstrap key?\n\n" +
      "This key will be embedded in the next desktop installer build. " +
      "Any installer already built with the previous key will still work " +
      "until you revoke the old token."
    )) return;
    setCreatingBoot(true);
    try {
      const r = await createBootstrapToken({ label: "Bootstrap " + new Date().toLocaleDateString() });
      setNewBootstrap(r.data.token); await load();
    } catch(e) { console.error(e); alert("Failed to create bootstrap key"); }
    finally { setCreatingBoot(false); }
  };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:900 }}>
      <div style={T.card}>
        <div style={T.cardH}>Generate Provisioning Token</div>
        <p style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.7, marginBottom:14 }}>
          Provisioning tokens allow the MIDTC desktop app to self-register new devices. Each token is single-use and only grants access to the device registration endpoint.
        </p>
        <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ flex: isMobile ? "1 1 100%" : 1 }}>
            <label style={T.label}>Token Label (optional)</label>
            <input style={T.input} value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Acme Corp deployment" />
          </div>
          <button style={T.btn()} onClick={handleCreate} disabled={creating}>{creating ? <Spinner /> : "+ Generate Token"}</button>
        </div>
        {newToken && (
          <div style={{ marginTop:16, background:"#052e16", border:"1px solid #166534", borderRadius:8, padding:14 }}>
            <p style={{ fontSize:11, color:"var(--badge-green)", marginBottom:8, fontWeight:600 }}>✅ Token generated — copy it now, it won't be shown again:</p>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <code style={{ flex:1, fontSize:11, color:"#67e8f9", background:"#0a1a2e", padding:"8px 12px", borderRadius:6, wordBreak:"break-all" }}>{newToken}</code>
              <button style={T.btn("#0891b2")} onClick={() => navigator.clipboard.writeText(newToken)}>Copy</button>
            </div>
            <button style={{ ...T.btnSm(), marginTop:10 }} onClick={() => setNewToken(null)}>Dismiss</button>
          </div>
        )}
      </div>
      {/* RMM Bootstrap Key */}
      <div style={{ ...T.card, borderColor:"rgba(99,102,241,0.3)" }}>
        <div style={T.cardH}>
          <span>🤖 RMM Agent Bootstrap Key</span>
          {activeBoot && <span style={{ fontSize:11, color:"var(--badge-green)", fontWeight:500 }}>Active</span>}
        </div>
        <p style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.7, marginBottom:14 }}>
          This long-lived key is baked into the MIDTC desktop installer. When users install the app,
          the embedded agent uses this key to self-register with the backend and exchange it for a
          per-device API key (stored in DPAPI on Windows, file-scoped on macOS). The bootstrap key
          itself can only register devices — it cannot read data or dispatch commands.
        </p>
        {activeBoot ? (
          <div style={{ background:"#0a1a2e", border:`1px solid ${tokens.border.soft}`, borderRadius:8, padding:12, marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontSize:11, color:"var(--t-text-label)", marginBottom:3 }}>Label</div>
                <div style={{ fontSize:13, fontWeight:600, color:tokens.text.primary }}>{activeBoot.label || "—"}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"var(--t-text-label)", marginBottom:3 }}>Registrations</div>
                <div style={{ fontSize:13, fontWeight:600, color:"#22c55e" }}>{activeBoot.use_count || 0}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"var(--t-text-label)", marginBottom:3 }}>Last Used</div>
                <div style={{ fontSize:13, color:tokens.text.primary }}>{activeBoot.last_used_at ? fmtDate(activeBoot.last_used_at) : "Never"}</div>
              </div>
              <div>
                <div style={{ fontSize:11, color:"var(--t-text-label)", marginBottom:3 }}>Key</div>
                <code style={{ fontSize:11, color:"#67e8f9", fontFamily:"monospace" }}>••••{activeBoot.token?.slice(-8) || activeBoot.token_partial || "????"}</code>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ background:"#1a1308", border:"1px solid #78350f", borderRadius:8, padding:12, marginBottom:12, fontSize:12, color:"#fbbf24" }}>
            ⚠ No active bootstrap key. Generate one before building the desktop installer.
          </div>
        )}
        <button style={T.btn("#6366f1")} onClick={handleCreateBootstrap} disabled={creatingBoot}>
          {creatingBoot ? <Spinner /> : (activeBoot ? "↻ Rotate Bootstrap Key" : "+ Generate Bootstrap Key")}
        </button>
        {newBootstrap && (
          <div style={{ marginTop:16, background:"#052e16", border:"1px solid #166534", borderRadius:8, padding:14 }}>
            <p style={{ fontSize:11, color:"var(--badge-green)", marginBottom:8, fontWeight:600 }}>✅ Bootstrap key generated — copy it now, it won't be shown again:</p>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <code style={{ flex:1, fontSize:11, color:"#67e8f9", background:"#0a1a2e", padding:"8px 12px", borderRadius:6, wordBreak:"break-all" }}>{newBootstrap}</code>
              <button style={T.btn("#0891b2")} onClick={() => navigator.clipboard.writeText(newBootstrap)}>Copy</button>
            </div>
            <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:10, lineHeight:1.6 }}>
              The build pipeline will fetch this automatically — you don't need to paste it anywhere. Re-run the desktop installer build to embed it.
            </div>
            <button style={{ ...T.btnSm(), marginTop:10 }} onClick={() => setNewBootstrap(null)}>Dismiss</button>
          </div>
        )}
      </div>

      <div style={{ ...T.card, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={T.cardH}>Token History ({tokens.length})</div>
        {loading ? <div style={{ padding:40, textAlign:"center" }}><Spinner /></div> :
         tokens.length === 0 ? <p style={{ color:"var(--t-text-label)", fontSize:13 }}>No tokens yet.</p> : (
          <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
            <thead><tr>{["Label","Type","Token","Status","Created","Used","Actions"].map(h => <th key={h} style={T.th}>{h}</th>)}</tr></thead>
            <tbody>
              {tokens.map(t => (
                <tr key={t.id}>
                  <td style={{ ...T.td, fontWeight:500 }}>{t.label||"—"}</td>
                  <td style={T.td}>
                    {t.bootstrap
                      ? <span style={T.badge("#6366f1")}>BOOTSTRAP</span>
                      : <span style={T.badge("#64748b")}>device</span>}
                  </td>
                  <td style={{ ...T.td, fontFamily:"monospace", fontSize:11, color:"#67e8f9" }}>{"••••" + t.token_partial}</td>
                  <td style={T.td}><span style={T.badge(t.active ? "#22c55e" : "#64748b")}>{t.active ? "Active" : "Used / Revoked"}</span></td>
                  <td style={T.td}>{fmtDate(t.created_at)}</td>
                  <td style={T.td}>{t.bootstrap ? `${t.use_count||0}× / ${fmtDate(t.last_used_at) || "—"}` : fmtDate(t.used_at)}</td>
                  <td style={T.td}>{t.active && <button style={T.btnSm("#450a0a","#fca5a5")} onClick={() => setConfirm(t)}>Revoke</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {confirm && (
        <Confirm
          message={`Revoke "${confirm.label || "this token"}"? Any devices using it will no longer be able to register.`}
          onConfirm={async () => { try { await revokeProvisionToken(confirm.id); setConfirm(null); await load(); } catch(e){} }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// =============================================================
//  TAB 5 — CLIENT PORTALS
// =============================================================
const Section = ({ title, children }) => (
  <div style={{ marginBottom:28 }}>
    <div style={{ fontSize:11, fontWeight:700, color:"var(--t-text-label)", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>{title}</div>
    {children}
  </div>
);
const Field = ({ label, children, hint }) => (
  <div style={{ marginBottom:16 }}>
    <label style={{ display:"block", fontSize:12, fontWeight:600, color:"var(--t-text-muted)", marginBottom:6 }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:4 }}>{hint}</div>}
  </div>
);
const PortalInput = ({ value, onChange, placeholder, prefix, ...rest }) => (
  <div style={{ display:"flex", alignItems:"center", background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:8, overflow:"hidden" }}>
    {prefix && <span style={{ padding:"0 10px", fontSize:12, color:"var(--t-text-deep)", borderRight: "1px solid rgba(148,163,184,0.11)", whiteSpace:"nowrap" }}>{prefix}</span>}
    <input value={value} onChange={onChange} placeholder={placeholder} style={{ flex:1, background:"none", border:"none", outline:"none", padding:"9px 12px", color:"var(--t-text-primary)", fontSize:13 }} {...rest} />
  </div>
);
const ColorField = ({ label, value, onChange, hint }) => (
  <Field label={label} hint={hint}>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ position:"relative", width:36, height:36, borderRadius:8, overflow:"hidden", border: "1px solid var(--t-border-soft)", flexShrink:0 }}>
        <input type="color" value={value} onChange={onChange} style={{ position:"absolute", inset:"-4px", width:"calc(100% + 8px)", height:"calc(100% + 8px)", border:"none", cursor:"pointer", padding:0 }} />
      </div>
      <PortalInput value={value} onChange={onChange} placeholder="#4f6fff" />
    </div>
  </Field>
);

function PreviewModal({ branding, onClose }) {
  const bg      = branding.accent_color  || "#0f172a";
  const primary = branding.primary_color || "#4f6fff";
  const logo    = branding.logo_url ? `${API_URL}${branding.logo_url}` : null;
  const name    = branding.company_name  || "Client Portal";
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={onClose}>
      <div style={{ background:"var(--t-surface-mid)", borderRadius:16, overflow:"hidden", width:"100%", maxWidth:480, boxShadow:"0 25px 60px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:"16px 20px", borderBottom:"1px solid #334155", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>Portal Preview</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--t-text-label)", cursor:"pointer" }}><X size={16} /></button>
        </div>
        <div style={{ background:bg }}>
          <div style={{ background:primary, padding:"14px 24px", display:"flex", alignItems:"center", gap:12 }}>
            {logo ? <img src={logo} alt="logo" style={{ height:32, maxWidth:120, objectFit:"contain" }} /> : <div style={{ fontWeight:700, fontSize:16, color:"#fff" }}>{name}</div>}
          </div>
          <div style={{ padding:24 }}>
            <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:10, padding:20, marginBottom:16 }}>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:8 }}>Welcome message</div>
              <div style={{ fontSize:15, color:"#fff", fontWeight:500 }}>{branding.welcome_msg || "Welcome to the support portal."}</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {["My Tickets","New Ticket","My Devices","Profile"].map(l => (
                <div key={l} style={{ background:"rgba(255,255,255,0.05)", borderRadius:8, padding:"12px 16px", fontSize:13, color:"rgba(255,255,255,0.7)" }}>{l}</div>
              ))}
            </div>
            <div style={{ marginTop:16, background:primary, borderRadius:8, padding:"10px 16px", textAlign:"center", fontSize:13, fontWeight:600, color:"#fff" }}>Submit a Ticket</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmDelete({ name, deleting, onConfirm, onCancel }) {
  const isMobile = useIsMobile();
  const [typed, setTyped] = useState("");
  const match = typed.trim() === name.trim();
  return (
    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap: isMobile ? "wrap" : "nowrap" }}>
      <input
        value={typed} onChange={e => setTyped(e.target.value)}
        placeholder={`Type "${name}" to confirm`}
        style={{ flex: isMobile ? "1 1 100%" : 1, background:"var(--t-surface-mid)", border:`1px solid ${match?"#16a34a":"var(--t-border-strong)"}`, borderRadius:8, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:13, outline:"none" }}
      />
      <button onClick={onCancel} disabled={deleting} style={{ padding:"8px 14px", background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, color:"var(--t-text-muted)", fontSize:12, cursor:"pointer" }}>Cancel</button>
      <button onClick={onConfirm} disabled={!match || deleting} style={{ padding:"8px 14px", background:match?"#b91c1c":"#374151", border:"none", borderRadius:8, color:match?"#fff":"#64748b", fontSize:12, fontWeight:600, cursor:match&&!deleting?"pointer":"default" }}>
        {deleting ? "Removing..." : "Confirm Delete"}
      </button>
    </div>
  );
}

function ClientBrandingEditor({ client, onSaved, onDeleted }) {
  const isMobile = useIsMobile();
  const fileRef = useRef();
  const [branding,    setBranding]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [status,      setStatus]      = useState(null);
  const [preview,     setPreview]     = useState(false);
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  useEffect(() => {
    setLoading(true);
    getClientBranding(client.id)
      .then(r => setBranding(r.data))
      .catch(() => setBranding({ company_name:client.company_name, primary_color:"#4f6fff", accent_color:"#0f172a", welcome_msg:"Welcome to the support portal.", portal_slug:slugify(client.company_name), logo_url:null }))
      .finally(() => setLoading(false));
  }, [client.id]);

  const set = (key, val) => setBranding(b => ({...b,[key]:val}));

  const save = async () => {
    setSaving(true); setStatus(null);
    try {
      await updateClientBranding(client.id, { company_name:branding.company_name, primary_color:branding.primary_color, accent_color:branding.accent_color, welcome_msg:branding.welcome_msg, portal_slug:branding.portal_slug });
      setStatus("saved"); onSaved(client.id, branding.portal_slug);
      setTimeout(() => setStatus(null), 3000);
    } catch(err) { setStatus(err.response?.data?.error || "Save failed"); }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const res = await uploadClientLogo(client.id, file); set("logo_url", res.data.logo_url); setStatus("saved"); setTimeout(() => setStatus(null), 2000); }
    catch { setStatus("Logo upload failed"); }
    e.target.value = "";
  };

  const handleLogoDelete = async () => {
    try { await deleteClientLogo(client.id); set("logo_url", null); } catch { setStatus("Failed to remove logo"); }
  };

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await deleteClient(client.id);
      if (onDeleted) onDeleted(client.id);
    } catch (err) {
      setStatus(err.response?.data?.error || "Failed to remove client");
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"var(--t-text-deep)" }}>Loading branding...</div>;

  const portalUrl = branding.portal_slug ? `${window.location.origin}/clientportal/${branding.portal_slug}` : null;

  return (
    <div style={{ padding: isMobile ? 14 : "20px 24px" }}>
      {preview && <PreviewModal branding={branding} onClose={() => setPreview(false)} />}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:"var(--t-text-primary)" }}>{client.company_name}</div>
          {portalUrl && <a href={portalUrl} target="_blank" rel="noreferrer" style={{ fontSize:11, color:"#3b82f6", display:"flex", alignItems:"center", gap:4, marginTop:2, textDecoration:"none" }}>{portalUrl} <ExternalLink size={10} /></a>}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setPreview(true)} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, color:"var(--t-text-muted)", fontSize:12, cursor:"pointer" }}>
            <Eye size={13} /> Preview
          </button>
          <button onClick={save} disabled={saving} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", background:saving?"#1e3a5f":"#4f6fff", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:600, cursor:saving?"default":"pointer" }}>
            {saving ? "Saving..." : <><Check size={13} /> Save</>}
          </button>
        </div>
      </div>
      {status && (
        <div style={{ marginBottom:16, padding:"10px 14px", borderRadius:8, fontSize:12, background:status==="saved"?"#14532d":"#7f1d1d", color:status==="saved"?"#86efac":"#fca5a5", display:"flex", alignItems:"center", gap:8 }}>
          {status==="saved" ? <Check size={13} /> : <AlertCircle size={13} />}
          {status==="saved" ? "Changes saved successfully" : status}
        </div>
      )}
      <Section title="Identity">
        <Field label="Portal Display Name" hint="Shown in the portal header if no logo is uploaded">
          <PortalInput value={branding.company_name||""} onChange={e => set("company_name",e.target.value)} placeholder={client.company_name} />
        </Field>
        <Field label="Portal URL Slug" hint={`URL: ${window.location.origin}/clientportal/[slug]`}>
          <PortalInput value={branding.portal_slug||""} onChange={e => set("portal_slug",e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-"))} placeholder={slugify(client.company_name)} prefix="/clientportal/" />
        </Field>
        <Field label="Welcome Message" hint="Shown on the portal dashboard">
          <textarea value={branding.welcome_msg||""} onChange={e => set("welcome_msg",e.target.value)} placeholder="Welcome to the support portal." rows={2}
            style={{ width:"100%", background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:"9px 12px", color:"var(--t-text-primary)", fontSize:13, resize:"vertical", outline:"none", boxSizing:"border-box" }} />
        </Field>
      </Section>
      <Section title="Logo">
        {branding.logo_url ? (
          <div style={{ display:"flex", alignItems:"center", gap:16, padding:"12px 16px", background: "var(--t-panel)", borderRadius:10, border: "1px solid var(--t-border-soft)" }}>
            <img src={`${API_URL}${branding.logo_url}`} alt="logo" style={{ height:48, maxWidth:160, objectFit:"contain" }} />
            <div style={{ flex:1 }}><div style={{ fontSize:12, color:"var(--t-text-muted)" }}>Current logo</div><div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:2 }}>{branding.logo_url.split("/").pop()}</div></div>
            <button onClick={handleLogoDelete} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:"#7f1d1d", border:"none", borderRadius:6, color:"#fca5a5", fontSize:12, cursor:"pointer" }}>
              <Trash2 size={12} /> Remove
            </button>
          </div>
        ) : (
          <div>
            <input type="file" ref={fileRef} onChange={handleLogoUpload} accept="image/*" style={{ display:"none" }} />
            <button onClick={() => fileRef.current.click()} style={{ width:"100%", padding:"20px", background: "var(--t-panel)", border:"2px dashed var(--t-border-mid)", borderRadius:10, color:"var(--t-text-deep)", fontSize:13, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}
              onMouseEnter={e => e.currentTarget.style.borderColor="#3b82f6"} onMouseLeave={e => e.currentTarget.style.borderColor="var(--t-border-mid)"}>
              <Upload size={20} style={{ color:"#3b82f6" }} />
              <span>Click to upload logo</span>
              <span style={{ fontSize:11, color:"var(--t-border-strong)" }}>PNG, JPG, SVG, WebP — max 2MB</span>
            </button>
          </div>
        )}
      </Section>
      <Section title="Colors">
        <ColorField label="Primary Color" value={branding.primary_color||"#4f6fff"} onChange={e => set("primary_color",e.target.value)} hint="Used for buttons, links, and nav header background" />
        <ColorField label="Portal Background Color" value={branding.accent_color||"#0f172a"} onChange={e => set("accent_color",e.target.value)} hint="Used for the portal page background" />
      </Section>

      {/* Danger Zone */}
      <div style={{ marginTop:24, padding:"16px 20px", border:"1px solid #7f1d1d", borderRadius:10, background: "var(--t-panel)" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#ef4444", letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:8 }}>Danger Zone</div>
        {!confirmDel ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)" }}>Remove Client</div>
              <div style={{ fontSize:12, color:"var(--t-text-muted)", marginTop:2 }}>Permanently deletes this client, all their tickets, contacts, and devices. This cannot be undone.</div>
            </div>
            <button onClick={() => setConfirmDel(true)} style={{ flexShrink:0, marginLeft:16, padding:"8px 16px", background:"#7f1d1d", border:"1px solid #991b1b", borderRadius:8, color:"#fca5a5", fontSize:12, fontWeight:600, cursor:"pointer" }}>
              Remove Client
            </button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize:13, color:"#fca5a5", marginBottom:12 }}>
              Are you sure? Type <strong style={{ color:"#f87171" }}>{client.company_name}</strong> to confirm deletion.
            </div>
            <ConfirmDelete name={client.company_name} deleting={deleting} onConfirm={handleDeleteClient} onCancel={() => setConfirmDel(false)} />
          </div>
        )}
      </div>
    </div>
  );
}

function ClientPortals() {
  const isMobile = useIsMobile();
  const [clients,  setClients]  = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [slugMap,  setSlugMap]  = useState({});

  useEffect(() => {
    getAdminPortalSettings()
      .then(r => {
        setClients(r.data);
        const map = {};
        r.data.forEach(c => { if (c.portal_slug) map[c.id] = c.portal_slug; });
        setSlugMap(map);
        if (r.data.length > 0) setSelected(r.data[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c => c.company_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ flex:1, display:"flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ width: isMobile ? "100%" : 280, flexShrink:0, borderRight: isMobile ? "none" : "1px solid var(--t-border-soft)", borderBottom: isMobile ? "1px solid var(--t-border-soft)" : "none", display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--t-border-soft)" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..."
            style={{ width:"100%", background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:12, outline:"none", boxSizing:"border-box" }} />
        </div>
        <div style={{ flex: isMobile ? "none" : 1, maxHeight: isMobile ? 240 : "none", overflowY:"auto" }}>
          {loading ? <div style={{ padding:24, textAlign:"center", color:"var(--t-text-deep)", fontSize:13 }}>Loading clients...</div> :
           filtered.length === 0 ? <div style={{ padding:24, textAlign:"center", color:"var(--t-text-deep)", fontSize:13 }}>No clients found</div> :
           filtered.map(client => {
             const isSelected = selected?.id === client.id;
             const slug = slugMap[client.id];
             return (
               <button key={client.id} onClick={() => setSelected(client)} style={{ width:"100%", padding:"12px 16px", background:isSelected?"var(--t-surface-deep)":"none", border:"none", borderLeft:`3px solid ${isSelected?"#3b82f6":"transparent"}`, textAlign:"left", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                 <div>
                   <div style={{ fontSize:13, fontWeight:isSelected?600:400, color:isSelected?"var(--t-text-primary)":"var(--t-text-muted)" }}>{client.company_name}</div>
                   <div style={{ fontSize:11, color:slug?"#3b82f6":"var(--t-border-strong)", marginTop:2 }}>{slug ? `/${slug}` : "No slug set"}</div>
                 </div>
                 <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                   {client.primary_color && <div style={{ width:10, height:10, borderRadius:"50%", background:client.primary_color, border: "1px solid var(--t-border-soft)" }} />}
                   <ChevronRight size={14} color="var(--t-border-strong)" />
                 </div>
               </button>
             );
           })}
        </div>
      </div>
      <div style={{ flex: isMobile ? "none" : 1, overflowY: isMobile ? "visible" : "auto", background:"var(--t-page)" }}>
        {selected
          ? <ClientBrandingEditor
              key={selected.id}
              client={selected}
              onSaved={(id,slug) => setSlugMap(m => ({...m,[id]:slug}))}
              onDeleted={(id) => {
                const remaining = clients.filter(c => c.id !== id);
                setClients(remaining);
                setSelected(remaining[0] || null);
                setSlugMap(m => { const n = {...m}; delete n[id]; return n; });
              }}
            />
          : <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height: isMobile ? "auto" : "100%", padding: isMobile ? "40px 14px" : 0, gap:12, color:"var(--t-border-strong)" }}><Building2 size={40} /><span style={{ fontSize:14 }}>Select a client to configure their portal</span></div>
        }
      </div>
    </div>
  );
}

// =============================================================
//  TAB 6 — MESHCENTRAL
// =============================================================
function MeshCentralSettings() {
  const isMobile = useIsMobile();
  const [nodes,      setNodes]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [syncing,    setSyncing]    = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [search,     setSearch]     = useState("");
  const [lastFetch,  setLastFetch]  = useState(null);

  const loadNodes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMeshcentralNodes();
      setNodes(r.data.nodes || []);
      setLastFetch(new Date());
    } catch (e) {
      console.error("MeshCentral nodes error:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadNodes(); }, [loadNodes]);

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await syncMeshcentral();
      setSyncResult({ type:"ok", data: r.data });
      // Refresh node list after sync so matched status updates
      await loadNodes();
    } catch (e) {
      setSyncResult({ type:"err", msg: e?.response?.data?.error || "Sync failed. Check MeshCentral connection." });
    } finally { setSyncing(false); }
  };

  const filtered = nodes.filter(n => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (n.name        || "").toLowerCase().includes(q) ||
      (n.hostname    || "").toLowerCase().includes(q) ||
      (n.client_name || "").toLowerCase().includes(q) ||
      (n.os          || "").toLowerCase().includes(q)
    );
  });

  const matched   = nodes.filter(n => n.matched).length;
  const unmatched = nodes.filter(n => !n.matched).length;
  const online    = nodes.filter(n => n.online).length;

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1100 }}>

      {/* ── Header card ── */}
      <div style={T.card}>
        <div style={{ ...T.cardH, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <Monitor size={18} color="#3b82f6" />
            <span>MeshCentral Remote Management</span>
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button
              onClick={loadNodes}
              disabled={loading}
              style={T.btnSm("var(--t-surface-mid)","var(--t-text-muted)")}
            >
              {loading ? <Spinner /> : "↺ Refresh"}
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || loading}
              style={{ ...T.btn("#0f4c8a","#93c5fd"), border:"1px solid #4f6fff", display:"flex", alignItems:"center", gap:6 }}
            >
              {syncing ? <><Spinner /> Syncing...</> : "⟳ Sync Nodes to Devices"}
            </button>
          </div>
        </div>

        <p style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.7, marginBottom:16 }}>
          Sync matches MeshCentral nodes to device records by computer name. Once matched, the
          Remote Access button on device cards will launch a browser-based remote session.
          Run sync any time a new MeshAgent is installed on a client machine.
        </p>

        {/* Summary stats */}
        <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap:10, marginBottom: syncResult ? 16 : 0 }}>
          {[
            { label:"Total Nodes",   val:nodes.length,  color:"var(--t-text-primary)" },
            { label:"Online",        val:online,        color:"#22c55e" },
            { label:"Matched to DB", val:matched,       color:"#3b82f6" },
            { label:"Unmatched",     val:unmatched,     color: unmatched > 0 ? "#f59e0b" : "#64748b" },
          ].map(s => (
            <div key={s.label} style={{ background:"var(--t-page)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:"12px 16px" }}>
              <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.val}</div>
              <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Sync result banner */}
        {syncResult && (
          <div style={{
            background: syncResult.type === "ok" ? "#052e16" : "#450a0a",
            border:     `1px solid ${syncResult.type === "ok" ? "#166534" : "#7f1d1d"}`,
            borderRadius:8, padding:"12px 16px",
            display:"flex", alignItems:"flex-start", gap:10,
          }}>
            <span style={{ fontSize:16 }}>{syncResult.type === "ok" ? "✅" : "❌"}</span>
            <div style={{ flex:1 }}>
              {syncResult.type === "ok" ? (
                <>
                  <div style={{ fontSize:13, fontWeight:700, color:"var(--badge-green)", marginBottom:4 }}>Sync completed successfully</div>
                  <div style={{ fontSize:12, color:"var(--badge-green)", lineHeight:1.8 }}>
                    {syncResult.data.total} nodes scanned &nbsp;·&nbsp;
                    <span style={{ color:"#34d399" }}>{syncResult.data.matched} matched</span> &nbsp;·&nbsp;
                    <span style={{ color: syncResult.data.unmatched > 0 ? "#fbbf24" : "#34d399" }}>{syncResult.data.unmatched} unmatched</span>
                  </div>
                  {syncResult.data.unmatched > 0 && (
                    <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:4 }}>
                      Unmatched nodes have no device record with a matching name. Check the node names in MeshCentral match your device records.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ fontSize:13, fontWeight:700, color:"#f87171", marginBottom:4 }}>Sync failed</div>
                  <div style={{ fontSize:12, color:"#fca5a5" }}>{syncResult.msg}</div>
                </>
              )}
            </div>
            <button onClick={() => setSyncResult(null)} style={{ background:"none", border:"none", color:"var(--t-text-deep)", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
          </div>
        )}

        {lastFetch && (
          <div style={{ fontSize:11, color:"var(--t-border-strong)", marginTop:12 }}>
            Last fetched: {lastFetch.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* ── Node list ── */}
      <div style={{ ...T.card, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={{ ...T.cardH, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
          <span>Nodes ({filtered.length})</span>
          <input
            placeholder="Search nodes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...T.input, width: isMobile ? "100%" : 220, flex: isMobile ? "1 1 100%" : "none", padding:"6px 10px", fontSize:12 }}
          />
        </div>

        {loading ? (
          <div style={{ padding:40, textAlign:"center" }}><Spinner /></div>
        ) : filtered.length === 0 ? (
          <p style={{ color:"var(--t-text-label)", fontSize:13 }}>
            {search ? `No nodes matching "${search}"` : "No nodes found. Make sure MeshCentral is running and agents are installed."}
          </p>
        ) : (
          <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
            <thead>
              <tr>
                {["Node Name","Hostname","OS","Mesh Group","Status","DB Device","Client","Match"].map(h => (
                  <th key={h} style={T.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(n => (
                <tr key={n.node_id}>
                  <td style={{ ...T.td, fontWeight:600, fontFamily:"monospace", fontSize:12 }}>{n.name || "—"}</td>
                  <td style={{ ...T.td, fontFamily:"monospace", fontSize:11, color:"var(--t-text-label)" }}>{n.hostname || "—"}</td>
                  <td style={{ ...T.td, fontSize:11, color:"var(--t-text-muted)" }}>{n.os || "—"}</td>
                  <td style={{ ...T.td, fontSize:11, color:"var(--t-text-label)" }}>{n.mesh_name || "—"}</td>
                  <td style={T.td}>
                    <span style={T.badge(n.online ? "#22c55e" : "#64748b")}>
                      {n.online ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td style={{ ...T.td, fontSize:12 }}>{n.db_device_name || <span style={{ color:"var(--t-border-strong)" }}>—</span>}</td>
                  <td style={{ ...T.td, fontSize:12, color:"#f59e0b" }}>{n.client_name || <span style={{ color:"var(--t-border-strong)" }}>—</span>}</td>
                  <td style={T.td}>
                    {n.matched
                      ? <span style={T.badge("#22c55e")}>✓ Matched</span>
                      : <span style={T.badge("#f59e0b")}>⚠ Unmatched</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Setup guide ── */}
      <div style={{ ...T.card, borderColor:"#1e3a5f" }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#60a5fa", marginBottom:12 }}>
          🖥️ MeshAgent Deployment
        </div>
        <div style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.9 }}>
          <div>1. Log into MeshCentral at <a href="/mesh/" target="_blank" rel="noreferrer" style={{ color:"#3b82f6" }}>ticketing.example.com/mesh/</a></div>
          <div>2. Create a <strong style={{ color:"var(--t-text-muted)" }}>Device Group</strong> for each client company</div>
          <div>3. Click <strong style={{ color:"var(--t-text-muted)" }}>Add Agent</strong> → download the installer for Windows/Mac/Linux</div>
          <div>4. Deploy the installer on client machines — the agent will check in automatically</div>
          <div>5. Return here and click <strong style={{ color:"var(--badge-blue)" }}>⟳ Sync Nodes to Devices</strong> to link agents to device records</div>
          <div>6. Once matched, the <strong style={{ color:"var(--t-text-muted)" }}>Remote Access</strong> button on device cards will be active</div>
        </div>
      </div>

    </div>
  );
}

// =============================================================
//  TICKET IMPORT COMPONENT
//  Add this before the AdminSettings export default function
// =============================================================

const MIDTC_FIELDS = [
  { key:"subject",       label:"Subject *",       required:true  },
  { key:"description",   label:"Description",     required:false },
  { key:"status",        label:"Status",          required:false },
  { key:"priority",      label:"Priority",        required:false },
  { key:"category",      label:"Category",        required:false },
  { key:"contact_email", label:"Contact Email",   required:false },
  { key:"contact_name",  label:"Contact Name",    required:false },
  { key:"contact_first", label:"Contact First Name", required:false },
  { key:"contact_last",  label:"Contact Last Name",  required:false },
  { key:"created_at",    label:"Created Date",    required:false },
  { key:"source_id",     label:"Source Ticket ID",required:false },
];

// Known Atera field mappings (pre-filled for JSON mode)
const ATERA_DEFAULTS = {
  subject:       "ticketTitle",
  description:   "ticketDescription",
  status:        "ticketStatus",
  priority:      "ticketPriority",
  contact_email: "endUserEmail",
  contact_name:  "endUserFullName",
  created_at:    "createdOn",
  source_id:     "ticketID",
};

const STATUS_COLORS = { Open:"#3b82f6", "In Progress":"#f59e0b", "On Hold":"#8b5cf6", Resolved:"#10b981", Closed:"#64748b" };
const PRIO_COLORS   = { Critical:"#ef4444", High:"#f97316", Medium:"#eab308", Low:"#22c55e" };

const mapAteraStatus = s => ({ open:"Open","in progress":"In Progress",pending:"Open",resolved:"Resolved",closed:"Closed","on hold":"On Hold",new:"Open",waiting:"On Hold" }[(s||"open").toLowerCase()] || "Open");
const mapAteraPrio   = p => ({ critical:"Critical",high:"High",medium:"Medium",normal:"Medium",low:"Low" }[(p||"medium").toLowerCase()] || "Medium");

// ── Preview row as it will look in MIDTC ────────────────────────
function AteraPreviewRow({ t, index }) {
  const status   = mapAteraStatus(t.TicketStatus);
  const priority = mapAteraPrio(t.TicketPriority);
  const sc = STATUS_COLORS[status]   || "#64748b";
  const pc = PRIO_COLORS[priority]   || "#64748b";
  const name = [t.EndUserFirstName, t.EndUserLastName].filter(Boolean).join(" ") || t.EndUserEmail || "—";
  return (
    <tr style={{ background: index % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
      <td style={{ ...T.td, color:"#60a5fa", fontWeight:600, whiteSpace:"nowrap" }}>
        TKT-{String(index + 1).padStart(4,"0")}
      </td>
      <td style={{ ...T.td, maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.TicketTitle || "—"}</td>
      <td style={T.td}>
        <span style={{ background:sc+"22", color:sc, padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:600 }}>{status}</span>
      </td>
      <td style={T.td}>
        <span style={{ background:pc+"22", color:pc, padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:600 }}>{priority}</span>
      </td>
      <td style={{ ...T.td, fontSize:12, color:"var(--t-text-muted)" }}>{t.EndUserEmail || "—"}</td>
      <td style={{ ...T.td, fontSize:12, color:"var(--t-text-muted)" }}>{t.CustomerName || "—"}</td>
      <td style={{ ...T.td, fontSize:12, color:"var(--t-text-label)", whiteSpace:"nowrap" }}>
        {t.CreatedOn ? new Date(t.CreatedOn).toLocaleDateString() : "—"}
      </td>
    </tr>
  );
}

function TicketImport() {
  const isMobile = useIsMobile();
  const [mode,          setMode]          = useState("atera"); // "atera" | "json"

  // ── Atera mode state ──
  const [ateraKey,      setAteraKey]      = useState("");
  const [keySaved,      setKeySaved]      = useState(false);
  const [savingKey,     setSavingKey]     = useState(false);
  const [preview,       setPreview]       = useState(null);  // { tickets, totalCount, existingCount }
  const [loadingPreview,setLoadingPreview]= useState(false);
  const [clearFirst,      setClearFirst]      = useState(false);
  const [importing,       setImporting]       = useState(false);
  const [importProgress,  setImportProgress]  = useState(null);
  const [result,          setResult]          = useState(null);
  const [ateraStep,       setAteraStep]       = useState(1); // 1=key, 2=preview, 3=confirm, 4=done
  const [emailPaused,     setEmailPaused]     = useState(false);
  const [togglingEmail,   setTogglingEmail]   = useState(false);

  // ── JSON mode state ──
  const [file,          setFile]          = useState(null);
  const [rawData,       setRawData]       = useState(null);
  const [jsonFields,    setJsonFields]    = useState([]);
  const [mapping,       setMapping]       = useState({});
  const [jsonPreview,   setJsonPreview]   = useState([]);
  const [clients,       setClients]       = useState([]);
  const [defaultClient, setDefaultClient] = useState("");
  const [jsonStep,      setJsonStep]      = useState(1); // 1=upload, 2=map, 3=preview, 4=done
  const [jsonResult,    setJsonResult]    = useState(null);

  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const API   = process.env.REACT_APP_API_URL + "/api";
  const token = () => localStorage.getItem("midtc_token") || "";
  const hdr   = () => ({ "Content-Type":"application/json", Authorization:`Bearer ${token()}` });

  useEffect(() => {
    fetch(`${API}/clients`, { headers: hdr() })
      .then(r => r.json()).then(d => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    // Check if Atera key is already saved
    fetch(`${API}/atera/preview`, { headers: hdr() })
      .then(r => r.json())
      .then(d => { if (d.tickets) { setKeySaved(true); setPreview(d); setAteraStep(2); } })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Atera: save key ──────────────────────────────────────────
  const handleSaveKey = async () => {
    if (!ateraKey.trim()) return;
    setSavingKey(true); setError("");
    try {
      const res = await fetch(`${API}/atera/save-key`, { method:"POST", headers:hdr(), body:JSON.stringify({ key: ateraKey.trim() }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed to save key");
      setKeySaved(true);
      await handleFetchPreview();
    } catch (err) { setError(err.message); }
    finally { setSavingKey(false); }
  };

  // ── Atera: fetch preview ─────────────────────────────────────
  const handleFetchPreview = async () => {
    setLoadingPreview(true); setError(""); setPreview(null);
    try {
      const res = await fetch(`${API}/atera/preview`, { headers: hdr() });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || "Preview failed");
      setPreview(d);
      setAteraStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoadingPreview(false); }
  };

  // ── Atera: run import (async with polling) ───────────────────
  const startImport = async (cf) => {
    setImporting(true); setError(""); setImportProgress(null);
    // Auto-pause email polling before import
    if (!emailPaused) {
      await fetch(`${API}/admin/email-polling`, { method:"PATCH", headers:hdr(), body:JSON.stringify({ paused: true }) }).catch(() => {});
      setEmailPaused(true);
    }
    try {
      const res = await fetch(`${API}/atera/import`, { method:"POST", headers:hdr(), body:JSON.stringify({ clearFirst: cf }) });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || "Import failed");
      const { jobId } = d;
      const poll = setInterval(async () => {
        try {
          const sr = await fetch(`${API}/atera/import-status/${jobId}`, { headers: hdr() });
          const s  = await sr.json();
          setImportProgress(s);
          if (s.status === "complete") {
            clearInterval(poll);
            setResult(s.result);
            setAteraStep(4);
            setImporting(false);
          } else if (s.status === "error") {
            clearInterval(poll);
            setError(s.error || "Import failed");
            setImporting(false);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) { setError(err.message); setImporting(false); }
  };

  const handleAteraImport = () => startImport(clearFirst);

  // ── Email polling toggle ──────────────────────────────────────
  const toggleEmailPause = async () => {
    setTogglingEmail(true);
    try {
      const res = await fetch(`${API}/admin/email-polling`, { method:"PATCH", headers:hdr(), body:JSON.stringify({ paused: !emailPaused }) });
      const d   = await res.json();
      setEmailPaused(d.paused);
    } catch { /* ignore */ }
    finally { setTogglingEmail(false); }
  };

  // ── JSON mode helpers ────────────────────────────────────────
  const extractKeys = (obj, prefix = "") => {
    const keys = [];
    for (const [k, v] of Object.entries(obj || {})) {
      const full = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) keys.push(...extractKeys(v, full));
      else keys.push(full);
    }
    return keys;
  };

  const handleFile = (f) => {
    setFile(f); setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let parsed = JSON.parse(e.target.result);
        if (!Array.isArray(parsed)) { const key = Object.keys(parsed).find(k => Array.isArray(parsed[k])); parsed = key ? parsed[key] : [parsed]; }
        if (!parsed.length) { setError("JSON file is empty."); return; }
        setRawData(parsed);
        const keys = [...new Set(parsed.slice(0,5).flatMap(r => extractKeys(r)))].sort();
        setJsonFields(keys);
        const isAtera = keys.some(k => ["ticketTitle","ticketID","endUserEmail"].includes(k));
        const autoMap = {};
        if (isAtera) { Object.assign(autoMap, ATERA_DEFAULTS); }
        else { for (const mf of MIDTC_FIELDS) { const match = keys.find(k => k.toLowerCase().includes(mf.key.replace("_"," ")) || k.toLowerCase().includes(mf.key)); if (match) autoMap[mf.key] = match; } }
        setMapping(autoMap);
        setJsonPreview(parsed.slice(0, 5));
        setJsonStep(2);
      } catch { setError("Invalid JSON file."); }
    };
    reader.readAsText(f);
  };

  const getVal = (row, path) => { if (!path) return null; let v = row; for (const p of path.split(".")) v = v?.[p]; return v ?? null; };

  const handleJsonImport = async () => {
    setImporting(true); setError("");
    try {
      const res = await fetch(`${API}/tickets/import`, { method:"POST", headers:hdr(), body:JSON.stringify({ tickets: rawData, mapping, default_client_id: defaultClient || undefined }) });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || "Import failed");
      setJsonResult(d);
      setJsonStep(4);
    } catch (err) { setError(err.message); }
    finally { setImporting(false); }
  };

  const resetAll = () => {
    setAteraStep(keySaved ? 2 : 1); setResult(null);
    setFile(null); setRawData(null); setJsonFields([]); setMapping({}); setJsonPreview([]); setJsonResult(null); setJsonStep(1);
    setError("");
  };

  const selStyle = { ...T.input, cursor:"pointer" };

  // ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:960 }}>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:"var(--t-text-primary)" }}>🎫 Ticket Import</h2>
        <p style={{ margin:0, fontSize:13, color:"var(--t-text-label)" }}>Pull tickets directly from Atera via API, or import from a JSON file export. Contacts and clients are matched automatically.</p>
      </div>

      {/* Mode toggle */}
      <div style={{ display:"flex", gap:0, marginBottom:24, background:"var(--t-page)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:4, width:"fit-content" }}>
        {[{id:"atera",label:"Atera API"},{id:"json",label:"JSON File"}].map(m => (
          <button key={m.id} onClick={() => { setMode(m.id); setError(""); }}
            style={{ ...T.btn(mode===m.id?"#4f6fff":"transparent", mode===m.id?"#fff":"#64748b"), borderRadius:6, padding:"6px 18px", fontSize:13 }}>
            {m.label}
          </button>
        ))}
      </div>

      {error && <div style={T.err}>⚠ {error}</div>}

      {/* Email pause banner */}
      {mode === "atera" && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0, background: emailPaused ? "rgba(234,179,8,0.08)" : "rgba(30,41,59,0.5)", border:`1px solid ${emailPaused?"rgba(234,179,8,0.3)":"var(--t-surface-mid)"}`, borderRadius:8, padding:"10px 16px", marginBottom:16 }}>
          <div style={{ fontSize:13, color: emailPaused ? "#fbbf24" : "#64748b" }}>
            {emailPaused ? "⏸ Email polling is paused — no tickets will be created from incoming emails." : "📧 Email polling is active."}
          </div>
          <button onClick={toggleEmailPause} disabled={togglingEmail}
            style={{ ...T.btn(emailPaused?"#78350f":"var(--t-surface-mid)", emailPaused?"#fcd34d":"#94a3b8"), padding:"5px 14px", fontSize:12 }}>
            {togglingEmail ? <Spinner/> : emailPaused ? "Resume Email" : "Pause Email"}
          </button>
        </div>
      )}

      {/* ══════════════ ATERA MODE ══════════════ */}
      {mode === "atera" && (
        <>
          {/* Step indicator */}
          <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            {[{n:1,l:"API Key"},{n:2,l:"Preview"},{n:3,l:"Confirm"},{n:4,l:"Done"}].map(s => (
              <div key={s.n} style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:24, height:24, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, background:ateraStep>=s.n?"#4f6fff":"var(--t-surface-mid)", color:ateraStep>=s.n?"#fff":"#475569" }}>
                  {ateraStep > s.n ? "✓" : s.n}
                </div>
                <span style={{ fontSize:12, color:ateraStep>=s.n?"var(--t-text-primary)":"var(--t-text-deep)" }}>{s.l}</span>
                {s.n < 4 && <span style={{ color:"var(--t-border-strong)" }}>→</span>}
              </div>
            ))}
          </div>

          {/* Step 1 — API Key */}
          {ateraStep === 1 && (
            <div style={T.card}>
              <div style={T.cardH}>Connect to Atera</div>
              <p style={{ fontSize:13, color:"var(--t-text-label)", marginBottom:20 }}>Enter your Atera API key. Find it in Atera → Admin → API. The key is saved securely and used for all future imports.</p>
              <label style={T.label}>Atera API Key</label>
              <div style={{ display:"flex", gap:8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <input
                  type="password"
                  placeholder="Paste your Atera API key here…"
                  value={ateraKey}
                  onChange={e => setAteraKey(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSaveKey()}
                  style={{ ...T.input, flex:1 }}
                />
                <button onClick={handleSaveKey} disabled={!ateraKey.trim() || savingKey} style={T.btn()}>
                  {savingKey ? <Spinner/> : "Connect →"}
                </button>
              </div>
              <p style={{ fontSize:12, color:"var(--t-text-deep)", marginTop:12 }}>The key is stored in your system settings and never exposed to the browser after this point.</p>
            </div>
          )}

          {/* Step 2 — Preview */}
          {ateraStep === 2 && preview && (
            <div style={T.card}>
              <div style={{ ...T.cardH, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
                Atera Ticket Preview
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={handleFetchPreview} disabled={loadingPreview} style={T.btn("var(--t-surface-mid)","var(--t-text-muted)")}>
                    {loadingPreview ? <Spinner/> : "↺ Refresh"}
                  </button>
                  <button onClick={() => setAteraStep(3)} style={T.btn()}>Proceed to Import →</button>
                </div>
              </div>

              {/* Stats row */}
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap:12, marginBottom:20 }}>
                {[
                  { label:"Tickets in Atera",    value: preview.totalCount,    color:"#60a5fa" },
                  { label:"Existing Tickets",     value: preview.existingCount, color:"#f59e0b" },
                  { label:"Showing Preview",      value: preview.tickets?.length || 0, color:"var(--t-text-muted)" },
                ].map(s => (
                  <div key={s.label} style={{ background:"var(--t-page)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:"14px 16px" }}>
                    <div style={{ fontSize:26, fontWeight:700, color:s.color }}>{s.value?.toLocaleString()}</div>
                    <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <p style={{ fontSize:12, color:"var(--t-text-label)", marginBottom:12 }}>
                Showing first {preview.tickets?.length} of {preview.totalCount?.toLocaleString()} tickets — as they will appear in MIDTC after import. Ticket numbers are assigned in chronological order (oldest = TKT-0001).
              </p>

              <div style={{ overflowX:"auto" }}>
                <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
                  <thead>
                    <tr>
                      {["MIDTC #","Subject","Status","Priority","Contact Email","Customer","Created"].map(h => (
                        <th key={h} style={T.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.tickets || []).map((t, i) => (
                      <AteraPreviewRow key={t.TicketID} t={t} index={i} />
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.totalCount > (preview.tickets?.length || 0) && (
                <div style={{ textAlign:"center", fontSize:12, color:"var(--t-text-deep)", padding:"12px 0 0" }}>
                  … and {(preview.totalCount - preview.tickets.length).toLocaleString()} more tickets
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Confirm */}
          {ateraStep === 3 && preview && (
            <div style={T.card}>
              <div style={T.cardH}>Confirm Import</div>

              {/* Clear existing warning */}
              <div style={{ background: clearFirst ? "rgba(220,38,38,0.08)" : "rgba(15,23,42,0.5)", border:`1px solid ${clearFirst?"rgba(220,38,38,0.3)":"var(--t-surface-mid)"}`, borderRadius:10, padding:16, marginBottom:20 }}>
                <label style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
                  <input type="checkbox" checked={clearFirst} onChange={e => setClearFirst(e.target.checked)}
                    style={{ width:16, height:16, marginTop:2, accentColor:"#ef4444", flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color: clearFirst ? "#f87171" : "#94a3b8", marginBottom:4 }}>
                      Clear all existing tickets before importing
                    </div>
                    {clearFirst && (
                      <div style={{ fontSize:12, color:"#f87171" }}>
                        ⚠ This will permanently delete <strong>{preview.existingCount?.toLocaleString()}</strong> existing tickets and all their responses, comments, and history. This cannot be undone.
                      </div>
                    )}
                    {!clearFirst && (
                      <div style={{ fontSize:12, color:"var(--t-text-label)" }}>
                        Tickets already imported (matched by Atera ID) will be skipped. New tickets will be appended.
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Summary */}
              <div style={{ background:"rgba(29,78,216,0.06)", border:"1px solid rgba(29,78,216,0.2)", borderRadius:8, padding:"14px 16px", marginBottom:20, fontSize:13, color:"var(--badge-blue)" }}>
                <div style={{ fontWeight:700, marginBottom:8 }}>Import Summary</div>
                <div>• <strong>{preview.totalCount?.toLocaleString()}</strong> tickets created in the last <strong>{preview.windowDays || 90}</strong> days will be imported{clearFirst ? "" : " — already-imported tickets are skipped"}</div>
                {clearFirst && <div>• <strong>{preview.existingCount?.toLocaleString()}</strong> existing tickets will be deleted first</div>}
                <div>• Tickets imported oldest-first — lowest TKT number = oldest ticket in the window</div>
                <div>• Ticket counter will be set to <strong>Atera's highest ticket ID</strong> — new tickets continue from there</div>
                <div>• Contacts auto-created by email; clients matched by domain or company name</div>
              </div>

              {/* Progress bar while importing */}
              {importing && importProgress && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--t-text-label)", marginBottom:6 }}>
                    <span>{importProgress.phase || "Running…"}</span>
                    <span>{importProgress.progress?.toLocaleString()} / {importProgress.total?.toLocaleString()}</span>
                  </div>
                  <div style={{ background:"var(--t-surface-mid)", borderRadius:6, height:8, overflow:"hidden" }}>
                    <div style={{
                      height:"100%", borderRadius:6, background:"linear-gradient(90deg,#4f6fff,#3b82f6)",
                      width: importProgress.total > 0 ? `${Math.round((importProgress.progress/importProgress.total)*100)}%` : "5%",
                      transition:"width 0.5s ease",
                    }}/>
                  </div>
                </div>
              )}

              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                <button onClick={() => setAteraStep(2)} disabled={importing} style={T.btn("var(--t-surface-mid)","var(--t-text-muted)")}>← Back</button>
                <button
                  onClick={handleAteraImport}
                  disabled={importing}
                  style={T.btn(clearFirst?"#dc2626":"#4f6fff","#fff")}
                >
                  {importing
                    ? <><Spinner/>&nbsp;{importProgress?.total > 0 ? `${Math.round((importProgress.progress/importProgress.total)*100)}%` : "Starting…"}</>
                    : clearFirst
                      ? `⚠ Clear & Import ${preview.totalCount?.toLocaleString()} Tickets`
                      : `✅ Import ${preview.totalCount?.toLocaleString()} Tickets`
                  }
                </button>
                {!importing && (
                  <button
                    onClick={() => startImport(false)}
                    style={T.btn("#166534","#4ade80")}
                    title="Skip already-imported tickets and continue from where a previous import left off"
                  >
                    ↩ Resume / Top-up
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Step 4 — Done */}
          {ateraStep === 4 && result && (
            <div style={T.card}>
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
                <h2 style={{ color:"var(--badge-green)", margin:"0 0 8px" }}>Import Complete!</h2>
                <p style={{ color:"var(--t-text-label)", marginBottom:24 }}>{result.message}</p>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap:12, marginBottom:24 }}>
                  {[
                    { label:"Imported",         value:result.imported,          color:"var(--badge-green)" },
                    { label:"Skipped",           value:result.skipped,           color:"var(--t-text-muted)" },
                    { label:"Contacts Created",  value:result.contacts_created,  color:"#60a5fa" },
                    { label:"Next Ticket #",     value:`TKT-${String(result.nextTicketNumber||1).padStart(4,"0")}`, color:"#f59e0b" },
                  ].map(s => (
                    <div key={s.label} style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:12 }}>
                      <div style={{ fontSize:s.label==="Next Ticket #"?16:28, fontWeight:700, color:s.color }}>{s.value}</div>
                      <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {result.errors?.length > 0 && (
                  <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, padding:14, textAlign:"left", marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#f87171", marginBottom:8 }}>Errors ({result.errors.length}):</div>
                    {result.errors.slice(0,10).map((e,i) => <div key={i} style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>• {e.ticket}: {e.error}</div>)}
                    {result.errors.length > 10 && <div style={{ fontSize:12, color:"var(--t-text-deep)" }}>…and {result.errors.length - 10} more</div>}
                  </div>
                )}
                <button onClick={resetAll} style={T.btn()}>Run Another Import</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════ JSON MODE ══════════════ */}
      {mode === "json" && (
        <>
          {/* Step indicator */}
          <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap: isMobile ? "wrap" : "nowrap" }}>
            {[{n:1,l:"Upload"},{n:2,l:"Map Fields"},{n:3,l:"Preview"},{n:4,l:"Done"}].map(s => (
              <div key={s.n} style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:24, height:24, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, background:jsonStep>=s.n?"#4f6fff":"var(--t-surface-mid)", color:jsonStep>=s.n?"#fff":"#475569" }}>
                  {jsonStep > s.n ? "✓" : s.n}
                </div>
                <span style={{ fontSize:12, color:jsonStep>=s.n?"var(--t-text-primary)":"var(--t-text-deep)" }}>{s.l}</span>
                {s.n < 4 && <span style={{ color:"var(--t-border-strong)" }}>→</span>}
              </div>
            ))}
          </div>

          {/* Step 1 — Upload */}
          {jsonStep === 1 && (
            <div style={T.card}>
              <div style={T.cardH}>Upload JSON File</div>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                style={{ border:"2px dashed #334155", borderRadius:10, padding:40, textAlign:"center", cursor:"pointer", background:"var(--t-page)" }}
              >
                <div style={{ fontSize:40, marginBottom:12 }}>📂</div>
                <div style={{ fontSize:14, fontWeight:600, color:"var(--t-text-primary)", marginBottom:6 }}>Drop your JSON file here</div>
                <div style={{ fontSize:12, color:"var(--t-text-label)", marginBottom:16 }}>Atera exports auto-detect field mappings</div>
                <button style={T.btn()} onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}>Browse File</button>
                <input ref={fileRef} type="file" accept=".json" style={{ display:"none" }} onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }}/>
              </div>
              <div style={{ marginTop:16 }}>
                <label style={T.label}>Default Client (for unmatched contacts)</label>
                <select value={defaultClient} onChange={e => setDefaultClient(e.target.value)} style={selStyle}>
                  <option value="">Auto-detect by email domain</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Step 2 — Map Fields */}
          {jsonStep === 2 && (
            <div style={T.card}>
              <div style={T.cardH}>
                Map Fields
                <span style={{ fontSize:12, color:"var(--t-text-label)", fontWeight:400 }}>{rawData?.length} records · {file?.name}</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {MIDTC_FIELDS.map(mf => (
                  <div key={mf.key} style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "200px 1fr", alignItems:"center", gap: isMobile ? 4 : 12 }}>
                    <div style={{ fontSize:13, color:mf.required?"var(--t-text-primary)":"var(--t-text-muted)", fontWeight:mf.required?600:400 }}>
                      {mf.label}{mf.required && <span style={{ color:"#ef4444" }}> *</span>}
                    </div>
                    <select value={mapping[mf.key]||""} onChange={e => setMapping(p => ({...p,[mf.key]:e.target.value||undefined}))} style={selStyle}>
                      <option value="">— Not mapped —</option>
                      {jsonFields.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:10, marginTop:20 }}>
                <button onClick={() => setJsonStep(1)} style={T.btn("var(--t-surface-mid)","var(--t-text-muted)")}>← Back</button>
                <button onClick={() => setJsonStep(3)} disabled={!mapping.subject} style={T.btn()}>Preview →</button>
              </div>
            </div>
          )}

          {/* Step 3 — Preview */}
          {jsonStep === 3 && (
            <div style={T.card}>
              <div style={T.cardH}>
                Preview (first 5 records)
                <span style={{ fontSize:12, color:"var(--t-text-label)", fontWeight:400 }}>{rawData?.length} total records</span>
              </div>
              <div style={{ overflowX:"auto", marginBottom:20 }}>
                <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
                  <thead>
                    <tr>{["Subject","Status","Priority","Contact Email","Contact Name","Created"].map(h => <th key={h} style={T.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {jsonPreview.map((row,i) => (
                      <tr key={i}>
                        <td style={{ ...T.td, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{getVal(row,mapping.subject)||"—"}</td>
                        <td style={T.td}>{getVal(row,mapping.status)||"—"}</td>
                        <td style={T.td}>{getVal(row,mapping.priority)||"—"}</td>
                        <td style={T.td}>{getVal(row,mapping.contact_email)||"—"}</td>
                        <td style={T.td}>{getVal(row,mapping.contact_name)||`${getVal(row,mapping.contact_first)||""} ${getVal(row,mapping.contact_last)||""}`.trim()||"—"}</td>
                        <td style={T.td}>{getVal(row,mapping.created_at)?new Date(getVal(row,mapping.created_at)).toLocaleDateString():"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ ...T.ok, marginBottom:20 }}>
                ℹ Contacts auto-created if they don't exist. Duplicates (matching source ID) skipped.
                {defaultClient && clients.find(c=>c.id===defaultClient) && ` Unmatched → ${clients.find(c=>c.id===defaultClient).company_name}.`}
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setJsonStep(2)} style={T.btn("var(--t-surface-mid)","var(--t-text-muted)")}>← Back</button>
                <button onClick={handleJsonImport} disabled={importing} style={T.btn()}>
                  {importing ? <><Spinner/>&nbsp;Importing {rawData?.length} tickets…</> : `✅ Import ${rawData?.length} Tickets`}
                </button>
              </div>
            </div>
          )}

          {/* Step 4 — Done */}
          {jsonStep === 4 && jsonResult && (
            <div style={T.card}>
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
                <h2 style={{ color:"var(--badge-green)", margin:"0 0 8px" }}>Import Complete!</h2>
                <p style={{ color:"var(--t-text-label)", marginBottom:24 }}>{jsonResult.message}</p>
                <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap:12, marginBottom:24 }}>
                  {[
                    { label:"Imported",         value:jsonResult.imported,          color:"var(--badge-green)" },
                    { label:"Skipped",           value:jsonResult.skipped,           color:"var(--t-text-muted)" },
                    { label:"Contacts Created",  value:jsonResult.contacts_created,  color:"#60a5fa" },
                    { label:"Errors",            value:jsonResult.errors?.length||0, color:jsonResult.errors?.length?"#f87171":"#94a3b8" },
                  ].map(s => (
                    <div key={s.label} style={{ background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:8, padding:12 }}>
                      <div style={{ fontSize:28, fontWeight:700, color:s.color }}>{s.value}</div>
                      <div style={{ fontSize:11, color:"var(--t-text-label)", marginTop:4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {jsonResult.errors?.length > 0 && (
                  <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, padding:14, textAlign:"left", marginBottom:20 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#f87171", marginBottom:8 }}>Errors:</div>
                    {jsonResult.errors.map((e,i) => <div key={i} style={{ fontSize:12, color:"var(--t-text-muted)", marginBottom:4 }}>• {e.ticket}: {e.error}</div>)}
                  </div>
                )}
                <button onClick={resetAll} style={T.btn()}>Import Another File</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================
//  DESKTOP APP DOWNLOADS
// =============================================================
const PLATFORMS = [
  { id:"windows",      icon:"🪟", label:"Windows",               sub:"NSIS Installer (.exe) or App Icon (.ico)", color:"#0078d4", accept:".exe,.ico" },
  { id:"mac",          icon:"🍎", label:"macOS (Intel)",         sub:"Bundle (.dmg / .zip)",                     color:"#888",    accept:".dmg,.zip" },
  { id:"mac-arm64",    icon:"🍎", label:"macOS (Apple Silicon)", sub:"Bundle (.dmg / .zip)",                     color:"#888",    accept:".dmg,.zip" },
  { id:"linux",        icon:"🐧", label:"Linux",                 sub:"AppImage",                                 color:"#f97316", accept:".AppImage" },
  { id:"edr-windows",  icon:"🛡️", label:"MIDTC EDR Agent",      sub:"Endpoint protection agent for Windows (.exe)", color:"#6366f1", accept:".exe" },
];

function GuideScreenshots() {
  const isMobile = useIsMobile();
  const [files,     setFiles]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [msg,       setMsg]       = useState(null);
  const [copied,    setCopied]    = useState(null);
  const inputRef = useRef(null);

  const BASE = (API_URL || "").replace(/\/api\/?$/, "");

  const load = () => {
    fetch(`${API_URL}/api/admin/guide-screenshots`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("midtc_token")}` },
    }).then(r => r.json()).then(setFiles).catch(() => setFiles([]));
  };

  useEffect(load, []);

  const upload = (selected) => {
    if (!selected?.length) return;
    setMsg(null); setProgress(0); setUploading(true);
    const form = new FormData();
    Array.from(selected).forEach(f => form.append("files", f));
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/admin/guide-screenshots`);
    xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("midtc_token")}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 200) { setMsg({ ok: true, text: "Uploaded successfully" }); load(); }
      else { try { setMsg({ ok: false, text: JSON.parse(xhr.responseText).error || "Upload failed" }); } catch { setMsg({ ok: false, text: "Upload failed" }); } }
      if (inputRef.current) inputRef.current.value = "";
    };
    xhr.onerror = () => { setUploading(false); setMsg({ ok: false, text: "Upload failed" }); };
    xhr.send(form);
  };

  const remove = async (filename) => {
    await fetch(`${API_URL}/api/admin/guide-screenshots/${encodeURIComponent(filename)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${localStorage.getItem("midtc_token")}` },
    });
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(`${BASE}${url}`);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  };

  const isPdf = (filename) => /\.pdf$/i.test(filename);

  return (
    <div style={{ ...T.card, marginTop:28 }}>
      <div style={T.cardH}>
        <span>📸 Guide Assets</span>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <input type="file" accept="image/*,.pdf,.ico" multiple ref={inputRef} style={{ display:"none" }} onChange={e => upload(e.target.files)} />
          <button onClick={() => inputRef.current?.click()} disabled={uploading} style={{ ...T.btn("#4f6fff"), display:"flex", alignItems:"center", gap:6 }}>
            {uploading ? <><Spinner /> Uploading {progress}%</> : "⬆ Upload Images / PDF"}
          </button>
        </div>
      </div>
      {msg && <div style={msg.ok ? T.ok : T.err}>{msg.text}</div>}
      {uploading && (
        <div style={{ background:"var(--t-surface-mid)", borderRadius:4, height:6, overflow:"hidden", marginBottom:12 }}>
          <div style={{ width:`${progress}%`, height:"100%", background:"#4f6fff", transition:"width 0.2s" }} />
        </div>
      )}
      {files.length === 0 ? (
        <div style={{ fontSize:13, color:"var(--t-text-deep)", textAlign:"center", padding:"16px 0" }}>No assets uploaded yet</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {files.map(f => (
            <div key={f.filename} style={{ display:"flex", alignItems:"center", gap: isMobile ? 8 : 12, flexWrap: isMobile ? "wrap" : "nowrap", background:"var(--t-surface-mid)", borderRadius:8, padding:"8px 12px" }}>
              {isPdf(f.filename) ? (
                <div style={{ width:48, height:36, borderRadius:4, border:"1px solid var(--t-border-strong)", background: "var(--t-panel)",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <span style={{ fontSize:18 }}>📄</span>
                </div>
              ) : (
                <img src={`${BASE}${f.url}`} alt={f.filename} style={{ width:48, height:36, objectFit:"cover", borderRadius:4, border:"1px solid var(--t-border-strong)", flexShrink:0 }} />
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {f.filename}
                  {isPdf(f.filename) && <span style={{ marginLeft:6, fontSize:10, background:"rgba(99,162,255,0.15)", color:"var(--badge-blue)", borderRadius:4, padding:"1px 6px", fontWeight:500 }}>PDF</span>}
                </div>
                <div style={{ fontSize:11, color:"var(--t-text-deep)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{BASE}{f.url}</div>
              </div>
              {isPdf(f.filename) && (
                <a href={`${BASE}${f.url}`} target="_blank" rel="noopener noreferrer"
                  style={{ ...T.btnSm("#1e3a5f","#93c5fd"), textDecoration:"none", minWidth:52, textAlign:"center" }}>
                  View
                </a>
              )}
              <button onClick={() => copy(f.url)} style={{ ...T.btnSm(copied===f.url?"#14532d":"var(--t-surface-mid)", copied===f.url?"#86efac":"#94a3b8"), minWidth:52 }}>
                {copied===f.url ? "✓ Copied" : "Copy URL"}
              </button>
              <button onClick={() => remove(f.filename)} style={T.btnSm("#450a0a","#f87171")}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DesktopDownloads() {
  const isMobile = useIsMobile();
  const [info, setInfo]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl]           = useState({});
  const [ul, setUl]           = useState({});
  const [ulErr, setUlErr]     = useState({});
  const fileRefs              = useRef({});

  const reload = () => {
    setLoading(true);
    fetch(`${API_URL}/api/admin/downloads`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("midtc_token")}` },
    })
      .then(r => r.json())
      .then(d => { setInfo(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(reload, []);

  const download = (platform) => {
    const base = API_URL.replace(/\/api\/?$/, "");
    const link = document.createElement("a");
    link.href = `${base}/downloads/${platform}`;
    link.click();
  };

  const upload = (platform, file) => {
    if (!file) return;
    setUlErr(p => ({ ...p, [platform]: null }));
    setUl(p => ({ ...p, [platform]: 0 }));
    const token = localStorage.getItem("midtc_token");
    const form = new FormData();
    form.append("file", file);
    // Windows card accepts both .exe (installer) and .ico (app icon) — route by extension.
    const isIco    = /\.ico$/i.test(file.name);
    const endpoint = (platform === "windows" && isIco) ? "app-icon" : platform;
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}/api/admin/uploads/${endpoint}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUl(p => ({ ...p, [platform]: Math.round(e.loaded / e.total * 100) }));
    };
    xhr.onload = () => {
      setUl(p => ({ ...p, [platform]: null }));
      if (xhr.status === 200) { reload(); }
      else {
        try { setUlErr(p => ({ ...p, [platform]: JSON.parse(xhr.responseText).error || "Upload failed" })); }
        catch { setUlErr(p => ({ ...p, [platform]: "Upload failed" })); }
      }
      if (fileRefs.current[platform]) fileRefs.current[platform].value = "";
    };
    xhr.onerror = () => {
      setUl(p => ({ ...p, [platform]: null }));
      setUlErr(p => ({ ...p, [platform]: "Upload failed" }));
    };
    xhr.send(form);
  };

  return (
    <div style={{ maxWidth:680, margin: isMobile ? "16px auto" : "32px auto", padding: isMobile ? "0 14px" : "0 24px" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:"0 0 6px", fontSize:18, fontWeight:700, color:"var(--t-text-primary)" }}>Desktop App Downloads</h2>
        <p style={{ margin:0, fontSize:13, color:"var(--t-text-label)" }}>
          Upload built installers here so techs can download them. Build on Windows or Mac first, then upload the output file.
        </p>
      </div>
      {loading ? (
        <div style={{ textAlign:"center", padding:48, color:"var(--t-text-label)" }}><Spinner /></div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {PLATFORMS.map(p => {
            const entry   = info?.[p.id];
            const avail   = entry?.available;
            const sizeMB  = avail ? (entry.size / 1024 / 1024).toFixed(1) + " MB" : null;
            const built   = avail ? new Date(entry.mtime).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : null;
            const uploading = ul[p.id] != null;
            const pct       = ul[p.id];
            const errMsg    = ulErr[p.id];
            return (
              <div key={p.id} style={{ ...T.card, marginBottom:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap: isMobile ? 12 : 20, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                  <div style={{ fontSize:36, lineHeight:1 }}>{p.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:"var(--t-text-primary)", marginBottom:2 }}>{p.label}</div>
                    <div style={{ fontSize:12, color:"var(--t-text-label)" }}>{p.sub}</div>
                    {avail && (
                      <div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:4 }}>
                        {sizeMB} &bull; Updated {built}
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8, alignItems:"flex-end" }}>
                    {/* Upload button */}
                    <input
                      type="file"
                      accept={p.accept}
                      style={{ display:"none" }}
                      ref={el => fileRefs.current[p.id] = el}
                      onChange={e => upload(p.id, e.target.files[0])}
                    />
                    <button
                      onClick={() => fileRefs.current[p.id]?.click()}
                      disabled={uploading}
                      style={{ ...T.btn("#16a34a"), minWidth:120, display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}
                    >
                      {uploading ? <Spinner /> : "⬆ Upload"}
                    </button>
                    {/* Download button — only if available */}
                    {avail && (
                      <button
                        onClick={() => download(p.id)}
                        disabled={dl[p.id]}
                        style={{ ...T.btn(p.color), minWidth:120, display:"flex", alignItems:"center", gap:6, justifyContent:"center" }}
                      >
                        {dl[p.id] ? <Spinner /> : "⬇ Download"}
                      </button>
                    )}
                  </div>
                </div>
                {/* Upload progress bar */}
                {uploading && (
                  <div style={{ marginTop:12, background:"var(--t-surface-mid)", borderRadius:4, height:6, overflow:"hidden" }}>
                    <div style={{ width:`${pct}%`, height:"100%", background:"#16a34a", transition:"width 0.2s" }} />
                  </div>
                )}
                {errMsg && (
                  <div style={{ marginTop:8, fontSize:12, color:"#f87171" }}>{errMsg}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <GuideScreenshots />
      <div style={{ marginTop:28, background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius:8, padding:16 }}>
        <div style={{ fontSize:12, fontWeight:700, color:"var(--t-text-muted)", marginBottom:8 }}>Build &amp; upload instructions</div>
        <div style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.7 }}>
          1. Download source: <a href={`${API_URL.replace('/api','')}/downloads/source`} style={{ color:"var(--badge-blue)" }}>midtc-desktop-source.zip</a><br/>
          2. Run <code style={{ color:"var(--badge-blue)" }}>npm install</code> then <code style={{ color:"var(--badge-blue)" }}>npm run build:icons</code><br/>
          3. Windows: <code style={{ color:"var(--badge-blue)" }}>npm run build:win</code> &nbsp;•&nbsp; Mac: <code style={{ color:"var(--badge-blue)" }}>npm run build:mac</code><br/>
          4. Upload the <code style={{ color:"var(--badge-blue)" }}>.exe</code> or <code style={{ color:"var(--badge-blue)" }}>.dmg</code> from <code style={{ color:"var(--badge-blue)" }}>dist/</code> using the Upload button above.<br/>
          <span style={{ color:"#6366f1" }}>🛡️ EDR Agent:</span> Built automatically via GitHub Actions on push to <code style={{ color:"var(--badge-blue)" }}>edr/agent/**</code> — download from the Releases page or upload the built <code style={{ color:"var(--badge-blue)" }}>MIDTC-EDR-Setup.exe</code> directly.
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  MY PROFILE (signature + name)
// =============================================================
function MyProfile() {
  const isMobile = useIsMobile();
  const { agent, login, updateAgent } = useAuth();
  const [form,        setForm]        = useState({ first_name:"", last_name:"", signature:"" });
  const [saving,      setSaving]      = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [msg,         setMsg]         = useState(null);
  const [preview,     setPreview]     = useState(false);
  const sigImgRef = useRef(null);

  useEffect(() => {
    if (agent) setForm({
      first_name: agent.firstName || agent.first_name || "",
      last_name:  agent.lastName  || agent.last_name  || "",
      signature:  (agent.signature || "").replace(
        /(<img[^>]*?)style="[^"]*max-height:[^"]*"([^>]*>)/gi,
        '$1style="max-width:320px;width:100%;height:auto;display:block;margin-top:6px;"$2'
      ),
    });
  }, [agent]);

  const handleSigImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const res = await uploadSignatureImage(file);
      const imgTag = `<img src="${res.data.url}" alt="signature" style="max-width:320px;width:100%;height:auto;display:block;margin-top:6px;">`;
      setForm(f => ({ ...f, signature: f.signature + imgTag }));
      setMsg({ ok: true, text: "Image uploaded — it's been added to your signature." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Upload failed." });
    } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await updateMyProfile({ first_name: form.first_name, last_name: form.last_name, signature: form.signature });
      updateAgent({ ...res.data, firstName: res.data.first_name, lastName: res.data.last_name });
      setMsg({ ok: true, text: "Profile saved." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.error || "Failed to save." });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth:680, margin: isMobile ? "16px auto" : "32px auto", padding: isMobile ? "0 14px" : "0 24px" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:"0 0 6px", fontSize:18, fontWeight:700, color:"var(--t-text-primary)" }}>My Profile</h2>
        <p style={{ margin:0, fontSize:13, color:"var(--t-text-label)" }}>Update your name and set the signature that appears on outbound replies.</p>
      </div>

      {/* Name */}
      <div style={{ ...T.card, marginBottom:16 }}>
        <div style={T.cardH}><span>Name</span></div>
        <div style={T.grid2}>
          <div>
            <label style={T.label}>First Name</label>
            <input style={T.input} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
          </div>
          <div>
            <label style={T.label}>Last Name</label>
            <input style={T.input} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Signature */}
      <div style={T.card}>
        <div style={T.cardH}>
          <span>Email Signature</span>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={() => sigImgRef.current?.click()} disabled={uploading}
              style={T.btnSm("var(--t-surface-mid)", "#94a3b8")}>
              {uploading ? "Uploading…" : "Upload Image"}
            </button>
            <input ref={sigImgRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleSigImageUpload} />
            <button onClick={() => setPreview(p => !p)} style={T.btnSm(preview ? "#4f6fff" : "var(--t-surface-mid)", preview ? "#bfdbfe" : "#94a3b8")}>
              {preview ? "Edit" : "Preview"}
            </button>
          </div>
        </div>
        <p style={{ fontSize:12, color:"var(--t-text-label)", marginBottom:12 }}>
          Appended automatically to all public replies. Supports HTML — use <strong>Upload Image</strong> to add your signature image.
        </p>
        {preview ? (
          <div style={{ background:"var(--t-surface-mid)", borderRadius:8, padding:16, minHeight:80, fontSize:14, color:"var(--t-text-body)", lineHeight:1.7 }}
            dangerouslySetInnerHTML={{ __html: form.signature || "<em style='color:var(--t-text-deep)'>No signature set</em>" }} />
        ) : (
          <textarea
            value={form.signature}
            onChange={e => setForm(f => ({ ...f, signature: e.target.value }))}
            rows={6}
            placeholder={`<b>John Smith</b><br>MIDTC Support | IT Technician<br>📞 (555) 123-4567 | support@example.com`}
            style={{ ...T.input, resize:"vertical", fontFamily:"monospace", fontSize:12, lineHeight:1.6 }}
          />
        )}
        <p style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:8 }}>Tip: use &lt;b&gt;bold&lt;/b&gt;, &lt;br&gt; for line breaks, &lt;a href="..."&gt;links&lt;/a&gt;</p>
      </div>

      {/* Appearance — personal preference, saved to your profile only */}
      <div style={{ ...T.card, marginTop:16 }}>
        <div style={T.cardH}><span>Appearance</span></div>
        <p style={{ fontSize:12, color:"var(--t-text-label)", marginBottom:14 }}>
          Pick your theme and mode. This applies to <strong>your account only</strong> and saves instantly — other users keep their own.
        </p>
        <ThemePicker />
      </div>

      {msg && <div style={msg.ok ? T.ok : T.err}>{msg.text}</div>}
      <div style={{ marginTop:16 }}>
        <button onClick={save} disabled={saving} style={{ ...T.btn(), minWidth:120 }}>
          {saving ? <Spinner /> : "Save Profile"}
        </button>
      </div>
    </div>
  );
}

// =============================================================
// =============================================================
//  RMM DOWNLOADS
// =============================================================
function RmmDownloads() {
  const isMobile = useIsMobile();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const BASE = (API_URL || "").replace(/\/api\/?$/, "");

  const reload = () => {
    setLoading(true);
    setErr(null);
    getRmmStatus()
      .then(r => { setStatus(r.data); setLoading(false); })
      .catch(() => { setErr("Could not load RMM status"); setLoading(false); });
  };

  useEffect(reload, []);

  const dlBtn = (platform, label, os) => (
    <a
      href={`${BASE}/downloads/${platform}`}
      download
      style={{
        display:"flex", alignItems:"center", gap:12,
        padding:"14px 18px",
        background: tokens.surface.panel,
        border: `1px solid ${tokens.border.soft}`,
        borderRadius:10, textDecoration:"none",
        transition:"all 0.18s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = tokens.border.soft; }}
    >
      <div style={{ width:40, height:40, borderRadius:8, background:"rgba(99,102,241,0.12)", border:"1px solid rgba(99,102,241,0.25)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Monitor size={18} color="#818cf8"/>
      </div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:700, color:tokens.text.primary }}>{label}</div>
        <div style={{ fontSize:11, color:tokens.text.faint, marginTop:2 }}>{os}</div>
      </div>
      <span style={{ fontSize:11, color:"#818cf8", fontWeight:600 }}>Download →</span>
    </a>
  );

  const statCard = (val, label, color) => (
    <div style={{ textAlign:"center", padding:"14px 20px", background:"rgba(0,0,0,0.2)", borderRadius:8, border:`1px solid ${tokens.border.soft}` }}>
      <div style={{ fontSize:24, fontWeight:800, color: color || tokens.text.primary, lineHeight:1 }}>{val ?? "—"}</div>
      <div style={{ fontSize:11, color:tokens.text.faint, marginTop:4 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? 14 : 28, maxWidth:900 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, color:tokens.text.primary, margin:0 }}>RMM Agent</h2>
          <p style={{ color:tokens.text.faint, fontSize:13, marginTop:4 }}>Download the agent installer for client machines. Once installed, the agent checks in every 60 seconds.</p>
        </div>
        <button onClick={reload} style={T.btn("#334155","#94a3b8")}>↺ Refresh</button>
      </div>

      {/* Download cards */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap:12, marginBottom:24 }}>
        {dlBtn("rmm-windows", "MIDTC RMM Agent — Windows", "win-x64 · Self-contained EXE · .NET 8")}
        {dlBtn("rmm-mac",     "MIDTC RMM Agent — macOS",   "darwin arm64/x64 · launchd · .NET 8")}
        <a
          href={`${BASE}/downloads/rmm-agent`}
          style={{
            display:"flex", alignItems:"center", gap:12,
            padding:"14px 18px",
            background:"rgba(99,102,241,0.06)",
            border:"1px solid rgba(99,102,241,0.25)",
            borderRadius:10, textDecoration:"none",
            transition:"all 0.18s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.12)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(99,102,241,0.06)"; }}
        >
          <div style={{ width:40, height:40, borderRadius:8, background:"rgba(99,102,241,0.15)", border:"1px solid rgba(99,102,241,0.3)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <Monitor size={18} color="#818cf8"/>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"#818cf8" }}>Auto-Detect Download</div>
            <div style={{ fontSize:11, color:tokens.text.faint, marginTop:2 }}>OS detected from browser — share this link</div>
          </div>
          <span style={{ fontSize:11, color:"#818cf8", fontWeight:600 }}>→</span>
        </a>
      </div>

      {/* Live health */}
      <div style={{ ...T.card, marginBottom:0, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={{ ...T.cardH }}>
          <span>Live RMM Health</span>
          <span style={{ fontSize:11, color:tokens.text.faint, fontWeight:400 }}>Updates every minute</span>
        </div>
        {loading && <div style={{ color:tokens.text.faint, fontSize:13 }}>Loading...</div>}
        {err    && <div style={T.err}>{err}</div>}
        {!loading && !err && status && (
          <>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(6,1fr)", gap:10, marginBottom:16 }}>
              {statCard(status.devices?.total,        "Total devices",    tokens.text.primary)}
              {statCard(status.devices?.active_1h,    "Online (1h)",      "#22c55e")}
              {statCard(status.devices?.active_24h,   "Active (24h)",     "#818cf8")}
              {statCard(status.commands?.completed_24h,"Commands (24h)",  "#10b981")}
              {statCard(status.commands?.failed_24h,  "Failed (24h)",     (status.commands?.failed_24h || 0) > 0 ? "#ef4444" : tokens.text.faint)}
              {statCard(status.commands?.stuck,       "Stuck",            (status.commands?.stuck || 0) > 0 ? "#f59e0b" : tokens.text.faint)}
            </div>
            {/* Recent checkins */}
            {status.recent_checkins?.length > 0 && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:tokens.text.faint, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Recent Agent Check-ins</div>
                <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
                  <thead>
                    <tr>
                      <th style={T.th}>Device</th>
                      <th style={T.th}>Client</th>
                      <th style={T.th}>Last Seen</th>
                      <th style={T.th}>Version</th>
                      <th style={T.th}>CPU / RAM / Disk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.recent_checkins.map(d => (
                      <tr key={d.id}
                        onClick={() => window.location.href = `/devices/${encodeURIComponent(d.id)}`}
                        style={{ cursor:"pointer" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.05)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                      >
                        <td style={T.td}><span style={{ fontFamily:"monospace", fontSize:12 }}>{d.device_name || d.computer_name || "Unknown"}</span></td>
                        <td style={T.td}>{d.client_name || "—"}</td>
                        <td style={T.td}>{d.last_seen ? new Date(d.last_seen).toLocaleTimeString() : "—"}</td>
                        <td style={T.td}><span style={{ fontFamily:"monospace", fontSize:11 }}>{d.agent_version ? `v${d.agent_version}` : "—"}</span></td>
                        <td style={T.td}>
                          <span style={{ fontSize:11, color:tokens.text.faint }}>
                            {d.cpu_percent != null ? `CPU ${Math.round(d.cpu_percent)}%` : ""}
                            {d.ram_percent != null ? ` · RAM ${Math.round(d.ram_percent)}%` : ""}
                            {d.disk_used_percent != null ? ` · Disk ${Math.round(d.disk_used_percent)}%` : ""}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Install instructions */}
      <div style={{ ...T.card, marginTop:16 }}>
        <div style={T.cardH}>Quick Install</div>
        <div style={{ fontSize:12, color:tokens.text.faint, lineHeight:1.8 }}>
          <strong style={{ color:tokens.text.muted }}>Windows (PowerShell as Admin):</strong><br/>
          <code style={{ background:"rgba(0,0,0,0.3)", padding:"2px 8px", borderRadius:4, color:"#818cf8", display:"block", marginTop:4, marginBottom:10 }}>
            .\MIDTC.RMM.Agent.exe --install {window.location.origin}/api {"<agent-key>"}
          </code>
          <strong style={{ color:tokens.text.muted }}>macOS (Terminal as root):</strong><br/>
          <code style={{ background:"rgba(0,0,0,0.3)", padding:"2px 8px", borderRadius:4, color:"#34d399", display:"block", marginTop:4, marginBottom:10 }}>
            sudo ./MIDTC.RMM.Agent --install {window.location.origin}/api {"<agent-key>"}
          </code>
          <span style={{ color:tokens.text.deep }}>Get the agent key from Provisioning Tokens tab. The agent registers automatically and appears in Device Inventory.</span>
        </div>
      </div>
    </div>
  );
}

// =============================================================
//  SECURE LINKS TAB
// =============================================================
function SecureLinksTab() {
  const isMobile = useIsMobile();
  const [links,   setLinks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [modal,   setModal]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getSecureLinks(); setLinks(r.data); } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (token) => {
    try { await revokeSecureLink(token); setConfirm(null); await load(); } catch(e) { console.error(e); }
  };

  const fmtExpiry = (iso) => {
    const d = new Date(iso);
    if (d < new Date()) return <span style={T.badge("#64748b")}>Expired</span>;
    const ms = d - new Date();
    const h  = Math.ceil(ms / 3600000);
    return <span style={{ fontSize:11, color:"#34d399" }}>in {h >= 48 ? `${Math.ceil(h/24)}d` : `${h}h`}</span>;
  };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1100 }}>
      {modal && <SecureLinkModal onClose={() => { setModal(false); load(); }} />}

      <div style={{ ...T.card, overflowX: isMobile ? "auto" : "visible" }}>
        <div style={T.cardH}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <Lock size={15} color="#3b82f6" />
            <span>Secure Data Links ({links.length})</span>
          </div>
          <button style={T.btn()} onClick={() => setModal(true)}>+ Generate Link</button>
        </div>
        <p style={{ fontSize:12, color:"var(--t-text-label)", lineHeight:1.7, marginBottom:16 }}>
          Create time-limited, access-controlled links to securely share files or messages with clients.
        </p>
        {loading ? <div style={{ padding:40, textAlign:"center" }}><Spinner /></div> :
         links.length === 0 ? <p style={{ color:"var(--t-text-label)", fontSize:13 }}>No secure links created yet.</p> : (
          <table style={{ ...T.table, minWidth: isMobile ? 560 : undefined }}>
            <thead><tr>{["Title","Recipient","Views","Expires","Status","Created By","Actions"].map(h => <th key={h} style={T.th}>{h}</th>)}</tr></thead>
            <tbody>
              {links.map(l => {
                const expired   = new Date(l.expires_at) < new Date();
                const maxStr    = l.max_views === 0 ? "∞" : String(l.max_views);
                const statusClr = l.revoked ? "#ef4444" : expired ? "#64748b" : "#22c55e";
                const statusTxt = l.revoked ? "Revoked" : expired ? "Expired" : "Active";
                return (
                  <tr key={l.token}>
                    <td style={{ ...T.td, fontWeight:500, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.title || <em style={{ color:"var(--t-text-faint)" }}>Untitled</em>}</td>
                    <td style={{ ...T.td, fontSize:12 }}>{l.recipient_email || <em style={{ color:"var(--t-text-faint)" }}>—</em>}</td>
                    <td style={T.td}>{l.view_count} / {maxStr}</td>
                    <td style={T.td}>{fmtExpiry(l.expires_at)}</td>
                    <td style={T.td}><span style={T.badge(statusClr)}>{statusTxt}</span></td>
                    <td style={{ ...T.td, fontSize:12 }}>{l.created_by_name || "—"}</td>
                    <td style={T.td}>
                      <div style={{ display:"flex", gap:6 }}>
                        {!l.revoked && !expired && (
                          <a href={`/share/${l.token}`} target="_blank" rel="noreferrer"
                            style={{ ...T.btnSm("#1e3a5f","#93c5fd"), display:"inline-flex", alignItems:"center", gap:4, textDecoration:"none" }}>
                            <ExternalLink size={10}/> Open
                          </a>
                        )}
                        {!l.revoked && <button style={T.btnSm("#450a0a","#fca5a5")} onClick={() => setConfirm(l)}>Revoke</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {confirm && (
        <Confirm
          message={`Revoke "${confirm.title || "this link"}"? The recipient will no longer be able to access it.`}
          onConfirm={() => handleRevoke(confirm.token)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

//  ROOT — TAB SHELL
// =============================================================
const TABS = [
  { id:"profile",     label:"👤 My Profile"           },
  { id:"users",       label:"👥 User Management"      },
  { id:"sla",         label:"⏱ SLA Policies"          },
  { id:"general",     label:"⚙️ General Settings"     },
  { id:"portals",     label:"🌐 Client Portals"        },
  { id:"links",       label:"🔐 Secure Links"          },
  { id:"rmm",         label:"🤖 RMM Agent"             },
];

export default function AdminSettings() {
  const [tab, setTab] = useState("users");
  const isMobile = useIsMobile();
  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column", background:"var(--t-page)", color:"var(--t-text-primary)" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ padding: isMobile ? "14px 14px 0" : "24px 32px 0", borderBottom:`1px solid ${tokens.border.softer}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:4 }}>
          <Settings size={20} color="#3b82f6" />
          <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:tokens.text.primary }}>Admin Settings</h1>
        </div>
        <div style={{ display:"flex", gap:2, marginTop: isMobile ? 10 : 16, overflowX: isMobile ? "auto" : "visible", whiteSpace: isMobile ? "nowrap" : "normal" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding:"10px 18px", fontSize:13, fontWeight:600, cursor:"pointer",
              border:"none", background:"none",
              color:tab===t.id ? tokens.text.primary : tokens.text.label,
              borderBottom:tab===t.id?"2px solid #4f6fff":"2px solid transparent",
              marginBottom:-1,
              flexShrink: isMobile ? 0 : undefined,
              whiteSpace: isMobile ? "nowrap" : undefined,
            }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
        {tab === "profile"     && <div style={{ overflowY:"auto", flex:1 }}><MyProfile /></div>}
        {tab === "users"       && <div style={{ overflowY:"auto", flex:1 }}><UserManagement /></div>}
        {tab === "sla"         && <div style={{ overflowY:"auto", flex:1 }}><SLAPolicies /></div>}
        {tab === "general"     && <div style={{ overflowY:"auto", flex:1 }}><GeneralSettings /></div>}
        {tab === "tokens"      && <div style={{ overflowY:"auto", flex:1 }}><ProvisioningTokens /></div>}
        {tab === "portals"     && <div style={{ flex:1, display:"flex", overflow: isMobile ? "auto" : "hidden" }}><ClientPortals /></div>}
        {tab === "meshcentral" && <div style={{ overflowY:"auto", flex:1 }}><MeshCentralSettings /></div>}
        {tab === "import"     && <div style={{ overflowY:"auto", flex:1 }}><TicketImport /></div>}
        {tab === "downloads"  && <div style={{ overflowY:"auto", flex:1 }}><DesktopDownloads /></div>}
        {tab === "rmm"        && <div style={{ overflowY:"auto", flex:1 }}><RmmDownloads /></div>}
        {tab === "links"      && <div style={{ overflowY:"auto", flex:1 }}><SecureLinksTab /></div>}
      </div>
    </div>
  );
}
